import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DefaultNoteTextarea from "../common/DefaultNoteTextarea";
import VestaDateInput from "../common/VestaDateInput";
import VestaSelect from "../common/VestaSelect";
import { CustomerContactsList, CustomerDocumentsList } from "../common/CustomerRegistryLists";
import { formatVestaDate, normalizeVestaDateInput, parseVestaDate, toIsoVestaDate } from "../../utils/dateInput";
import { descriptionCatalogOptions, findMunicipality, isItaly, municipalityCatalogOptions, municipalityCodeForValue, postalCodeCatalogOptions } from "../../utils/customerCatalogs";
import { calculateItalianFiscalCode, canCalculateCustomerFiscalCode, getCustomerRegistryTypeFromCustomer, normalizeCustomerGender, requiredCustomerFields } from "../../utils/customerRegistryTypes";
import { OverlayFieldError, OverlayFieldLabel, overlayFieldClass } from "../common/OverlayValidation";
import BookingStayTabs from "./BookingStayTabs";
import { cancelBookingAdvanceDocument, cancelBookingAdvanceReversalDocument, getBookingAccounts, getBookingDocumentNumberPreview, getFiscalDocumentTypes, issueBookingAdvanceDocument, issueBookingAdvanceReversalDocument } from "../../api";
import { UtilityExportOverlay, UtilityFullscreenPreview, downloadBlob, getCompanyDisplayName, slugify } from "../../pages/UtilityWindows";
import { createFiscalVisualExportBlob, printFiscalPreviewRoot } from "../../utils/fiscalDocumentExport";
import vestaLogo from "../../assets/images/svg/logo_img.png";
import "../../assets/css/bookingAccounts.css";

const ROOM_GROUPS = [
  { type: "Matrimoniale", rooms: ["101", "102", "103", "104", "105"] },
  { type: "Doppia", rooms: ["201", "202", "203", "204", "205"] },
  { type: "Tripla", rooms: ["301", "302", "303", "304"] },
  { type: "Suite", rooms: ["401", "402"] },
  { type: "Singola", rooms: ["501", "502", "503"] },
];

const EMPTY_ROOM_DRAFT = {
  site_id: "",
  room_number: "",
  room_type_id: "",
  room_type: "",
  price: 0,
  beds: [],
  services: [],
  maintenances: [],
  cleanings: [],
  maintenance_edit_id: "",
  maintenance_date_start: "",
  maintenance_time_start: "",
  maintenance_date_end: "",
  maintenance_time_end: "",
  maintenance_description: "",
  cleaning_edit_id: "",
  cleaning_date_start: "",
  cleaning_time_start: "",
  cleaning_date_end: "",
  cleaning_time_end: "",
  cleaning_type: "TURNOVER",
  cleaning_task_status: "PLANNED",
  cleaning_priority: "NORMAL",
  cleaning_assigned_to: "",
  cleaning_blocks_room: true,
  cleaning_note: "",
};

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const WEEKDAYS = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
const FISCAL_NUMBERING_REFRESH_EVENT = "vesta:fiscal-numbering-refresh";

function notifyFiscalNumberingRefresh() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(FISCAL_NUMBERING_REFRESH_EVENT));
}
const STATUS_LABELS = {
  PENDING: "In attesa",
  CONFIRMED: "Confermata",
  CHECKED_IN: "In house",
  CHECKED_OUT: "Conclusa",
  CANCELLED: "Cancellata",
};

const STATUS_FILTER_OPTIONS = [
  { value: "ALL", label: "Tutti gli Stati" },
  { value: "PENDING", label: "In Attesa" },
  { value: "CONFIRMED", label: "Confermate" },
  { value: "CHECKED_IN", label: "In House" },
  { value: "CHECKED_OUT", label: "Concluse" },
  { value: "CANCELLED", label: "Cancellate" },
];

const STATUS_ALL_DOT_ORDER = ["CONFIRMED", "CHECKED_IN", "PENDING", "CHECKED_OUT", "PENDING"];

const EMPTY_BOOKING = {
  guest_name: "",
  guest_email: "",
  guest_phone: "",
  room_number: "101",
  room_type: "Matrimoniale",
  check_in: "",
  check_in_time: "12:00",
  check_out: "",
  check_out_time: "11:59",
  adults: 1,
  children: 0,
  animals: 0,
  status: "CONFIRMED",
  payments: [],
  deposit_amount: 0,
  document_type: "",
  document_number: "",
  payment_method: "",
  total_amount: 0,
  credit_card: "",
  credit_card_circuit_id: "",
  credit_card_number: "",
  credit_card_expiry: "",
  notes: "",
  customer_id: "",
  invoice_same_as_customer: true,
  invoice_customer_id: "",
  invoice_name: "",
  invoice_email: "",
  invoice_phone: "",
  guests: [],
  relatedGuests: [],
};

const EMPTY_BOOKING_PAYMENT = {
  paymentMethod: "",
  paymentType: "PAYMENT",
  notes: "",
  amount: 0,
  isDeposit: false,
  advanceDocument: {
    documentId: "",
    documentType: "INVOICE",
    numberingMode: "AUTO",
    documentNumber: "",
    emissionDate: "",
    note: "",
    orientation: "PORTRAIT",
    issued: false,
    reversalDocument: {
      documentId: "",
      documentType: "CREDIT_NOTE",
      numberingMode: "AUTO",
      documentNumber: "",
      emissionDate: "",
      note: "",
      orientation: "PORTRAIT",
      issued: false,
    },
  },
};

const BOOKING_PAYMENT_TYPE_OPTIONS = [
  { value: "ADVANCE", label: "Acconto" },
  { value: "DEPOSIT", label: "Caparra" },
  { value: "PAYMENT", label: "Pagamento" },
  { value: "BALANCE", label: "A Saldo" },
];

const PAYMENT_METHOD_FALLBACK_OPTIONS = [
  "Contanti",
  "POS / Carte",
  "Assegni",
  "Bonifico",
  "Ricevuta Bancaria",
];

const CHECKIN_DEFAULTS = {
  document_type: "Carta identità",
  document_number: "",
  payment_method: "",
};

const ROOM_STATUS_FALLBACK = {
  cleaning: "Pulizie ultimate",
  maintenance: "Nessuna manutenzione",
};

const EMPTY_ADDITIONAL_GUEST = {
  customer_id: "",
  customerName: "",
  relationshipType: "OTHER",
  included: true,
  isMinor: false,
  guardianCustomerId: "",
  guardianFullName: "",
  guardianRelation: "",
  guardianConsent: false,
  notes: "",
};
const RELATED_GUEST_TYPE_OPTIONS = [
  { value: "SPOUSE", label: "Coniuge" },
  { value: "PARTNER", label: "Partner" },
  { value: "CHILD", label: "Figlio/a" },
  { value: "PARENT", label: "Genitore" },
  { value: "SIBLING", label: "Fratello/Sorella" },
  { value: "RELATIVE", label: "Altro Familiare" },
  { value: "FRIEND", label: "Amico/a" },
  { value: "COLLEAGUE", label: "Collega" },
  { value: "COMPANION", label: "Accompagnatore" },
  { value: "GROUP_MEMBER", label: "Membro Gruppo" },
  { value: "ANIMAL", label: "Animale" },
  { value: "OTHER", label: "Altro" },
];
const GENDER_OPTIONS = [
  { value: "M", label: "Maschio" },
  { value: "F", label: "Femmina" },
];
const CUSTOMER_STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Attivo" },
  { value: "INACTIVE", label: "Non Attivo" },
  { value: "BLOCKED", label: "Bloccato" },
];

function cleanContactText(value) {
  return String(value || "").trim();
}

function normalizeContactType(value) {
  const text = cleanContactText(value);
  const token = text.toLowerCase();
  if (["telefono", "tel", "fisso"].includes(token)) return "Telefono";
  if (["cellulare", "mobile", "cell", "whatsapp"].includes(token)) return "Cellulare";
  if (["email", "mail", "e-mail"].includes(token)) return "Email";
  if (token === "pec") return "PEC";
  if (token === "fax") return "Fax";
  return text;
}

function normalizeCustomerContacts(source = {}) {
  const contacts = Array.isArray(source.contacts) ? source.contacts.map((contact) => ({
    tipoContatto: normalizeContactType(contact?.tipoContatto || contact?.contactType || contact?.type || contact?.tipo || ""),
    datoContatto: cleanContactText(contact?.datoContatto || contact?.contactValue || contact?.value || contact?.dato || ""),
    interlocutore: cleanContactText(contact?.interlocutore || contact?.interlocutor || contact?.referente || ""),
    primary: contact?.primary === true || contact?.principale === true,
  })) : [];
  if (!Array.isArray(source.contacts)) [["telefono", "Telefono"], ["cellulare", "Cellulare"], ["email", "Email"], ["pec", "PEC"], ["fax", "Fax"]].forEach(([field, tipoContatto]) => {
    const datoContatto = cleanContactText(source[field]);
    if (datoContatto && !contacts.some((contact) => contact.tipoContatto === tipoContatto && contact.datoContatto === datoContatto)) {
      contacts.push({ tipoContatto, datoContatto, interlocutore: "" });
    }
  });
  return contacts.filter((contact) => contact.tipoContatto && contact.datoContatto);
}

function contactValue(source = {}, types = []) {
  const wanted = types.map((type) => String(type || "").toLowerCase());
  return normalizeCustomerContacts(source).find((contact) => wanted.includes(String(contact.tipoContatto || "").toLowerCase()))?.datoContatto || "";
}

function primaryContactValue(source = {}, type = "") {
  const wanted = String(type || "").toLowerCase();
  const matches = normalizeCustomerContacts(source).filter((contact) => String(contact.tipoContatto || "").toLowerCase() === wanted);
  return matches.find((contact) => contact.primary === true)?.datoContatto || "";
}

function phoneMobileValue(source = {}) {
  const phone = cleanContactText(primaryContactValue(source, "Telefono"));
  const mobile = cleanContactText(primaryContactValue(source, "Cellulare"));
  if (phone && mobile) return `${phone} - ${mobile}`;
  return phone || mobile || "";
}

function normalizeCustomerBoolean(value, fallback = false) {
  if (value === true || value === false) return value;
  const token = cleanContactText(value).toLowerCase();
  if (["true", "1", "s", "si", "sì", "yes"].includes(token)) return true;
  if (["false", "0", "n", "no"].includes(token)) return false;
  return fallback;
}

function cleanGuestText(value) {
  return String(value || "").trim();
}

function normalizeGuestRole(value) {
  const text = cleanGuestText(value).toUpperCase();
  if (!text) return "";
  const legacyMap = {
    FAMILY_MEMBER: "RELATIVE",
    FAMILY_HEAD: "RELATIVE",
    GROUP_HEAD: "GROUP_MEMBER",
    SINGLE: "OTHER",
  };
  const normalized = legacyMap[text] || text;
  const allowed = RELATED_GUEST_TYPE_OPTIONS.map((option) => option.value);
  return allowed.includes(normalized) ? normalized : "OTHER";
}

function customerDisplayName(customer = {}) {
  return cleanGuestText(customer.businessName || customer.ragioneSociale)
    || [customer.surname || customer.cognome, customer.name || customer.nome].map(cleanGuestText).filter(Boolean).join(" ")
    || cleanGuestText(customer.customerName || customer.guestName);
}

function customerRegistryNotes(customer = {}) {
  return cleanGuestText(customer.notes || customer.note);
}

function customerFiscalReference(customer = {}) {
  const fiscalCode = cleanGuestText(customer.fiscalCode || customer.codiceFiscale);
  const vatNumber = cleanGuestText(customer.vatNumber || customer.partitaIva);
  if (fiscalCode && vatNumber && fiscalCode.toUpperCase() === vatNumber.toUpperCase()) return `C.F./P.I. ${fiscalCode}`;
  return [fiscalCode ? `C.F. ${fiscalCode}` : "", vatNumber ? `P.I. ${vatNumber}` : ""].filter(Boolean).join(" - ");
}

function customerAddressSummary(customer = {}) {
  const address = cleanGuestText(customer.address || customer.indirizzo);
  const streetNumber = cleanGuestText(customer.streetNumber || customer.civico);
  const normalizedStreetNumber = streetNumber.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const visibleStreetNumber = normalizedStreetNumber === "SNC" ? "" : streetNumber;
  const province = cleanGuestText(customer.province || customer.provincia).toUpperCase();
  const city = cleanGuestText(customer.city || customer.citta);
  const addressLine = address && visibleStreetNumber
    ? `${address}, ${visibleStreetNumber}`
    : (address || visibleStreetNumber);
  const locality = [city, province ? `(${province})` : ""].map(cleanGuestText).filter(Boolean).join(" ");
  return [
    addressLine,
    locality,
    customer.postalCode || customer.cap,
    customer.country || customer.nazione,
  ].map(cleanGuestText).filter(Boolean).join(" - ");
}

function customerAutocompleteAddressSummary(customer = {}) {
  const address = cleanGuestText(customer.address || customer.indirizzo);
  const streetNumber = cleanGuestText(customer.streetNumber || customer.civico);
  const postalCode = cleanGuestText(customer.postalCode || customer.cap);
  const city = cleanGuestText(customer.city || customer.citta);
  const province = cleanGuestText(customer.province || customer.provincia).toUpperCase();
  const country = cleanGuestText(customer.country || customer.nazione);
  const addressLine = [address, streetNumber].filter(Boolean).join(", ");
  const locality = [postalCode, city, province ? `(${province})` : ""].filter(Boolean).join(" ");
  return [addressLine, locality, country].filter(Boolean).join(" - ");
}

function findCustomerById(customers = [], customerId = "") {
  const id = cleanGuestText(customerId);
  if (!id) return null;
  return (customers || []).find((customer) => String(customer._id || customer.id || "") === id) || null;
}

function normalizeAdditionalGuests(source = [], customers = []) {
  const rows = Array.isArray(source) ? source : [];
  return rows.map((guest) => {
    const customerId = cleanGuestText(guest?.customer_id || guest?.customerId || guest?.clienteId);
    const linkedCustomer = findCustomerById(customers, customerId);
    const fallbackName = cleanGuestText(guest?.customerName || guest?.guestName)
      || [guest?.surname || guest?.cognome, guest?.name || guest?.nome].map(cleanGuestText).filter(Boolean).join(" ");
    return {
      ...EMPTY_ADDITIONAL_GUEST,
      customer_id: customerId,
      customerName: customerDisplayName(linkedCustomer || {}) || fallbackName,
      relationshipType: normalizeGuestRole(guest?.relationshipType || guest?.guestRole || guest?.role || guest?.tipoOspite),
      included: guest?.included !== false,
      isMinor: guest?.isMinor === true || guest?.minorenne === true,
      guardianCustomerId: cleanGuestText(guest?.guardianCustomerId || guest?.guardian_customer_id || guest?.tutoreCustomerId),
      guardianFullName: cleanGuestText(guest?.guardianFullName || guest?.accompagnatore),
      guardianRelation: cleanGuestText(guest?.guardianRelation || guest?.rapportoAccompagnatore),
      guardianConsent: guest?.guardianConsent === true || guest?.assensoGenitori === true,
      notes: cleanGuestText(guest?.notes || guest?.note),
    };
  }).filter((guest) => guest.customer_id || guest.customerName || guest.guardianFullName || guest.notes);
}

function customerBirthDateValue(customer = {}) {
  return cleanGuestText(customer.birthDate || customer.dataNascita);
}

function isCustomerChildAtDate(customer = {}, referenceDate = "") {
  const rawBirthDate = customerBirthDateValue(customer);
  if (!rawBirthDate) return null;
  const birthDate = parseVestaDate(rawBirthDate);
  const onDate = parseVestaDate(referenceDate || new Date());
  if (!birthDate || !onDate) return null;
  let age = onDate.getFullYear() - birthDate.getFullYear();
  const monthDelta = onDate.getMonth() - birthDate.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && onDate.getDate() < birthDate.getDate())) age -= 1;
  return age < 18;
}

function deriveBookingOccupancyCounts(draft = {}, customers = []) {
  let adults = 0;
  let children = 0;
  let animals = 0;
  const referenceDate = draft.check_in || new Date();
  const primaryCustomerId = cleanGuestText(draft.customer_id);
  const primaryCustomer = findCustomerById(customers, primaryCustomerId);

  if (primaryCustomerId || cleanGuestText(draft.guest_name)) {
    const primaryIsChild = primaryCustomer ? isCustomerChildAtDate(primaryCustomer, referenceDate) : false;
    if (primaryIsChild === true) children += 1;
    else adults += 1;
  }

  (Array.isArray(draft.relatedGuests) ? draft.relatedGuests : []).forEach((guest) => {
    const role = normalizeGuestRole(guest?.relationshipType || guest?.guestRole);
    if (guest?.included !== true) return;
    if (role === "ANIMAL") {
      animals += 1;
      return;
    }
    const customerId = cleanGuestText(guest?.customer_id || guest?.customerId);
    if (!customerId || customerId === primaryCustomerId) return;
    const customer = findCustomerById(customers, customerId);
    const ageBasedChild = customer ? isCustomerChildAtDate(customer, referenceDate) : null;
    const isChild = ageBasedChild === null ? (guest?.isMinor === true || role === "CHILD") : ageBasedChild;
    if (isChild) children += 1;
    else adults += 1;
  });

  return { adults, children, animals };
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date, amount) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

function addMonths(date, amount) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function toIsoDate(date) {
  const value = new Date(date);
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function toIsoDateTime(date, hour, minute = 0) {
  const value = startOfDay(new Date(date));
  value.setHours(hour, minute, 0, 0);
  const isoDate = toIsoDate(value);
  return `${isoDate}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00`;
}

function toCheckInDateTime(date) {
  return toIsoDateTime(date, 12, 0);
}

function toCheckOutDateTime(date) {
  return toIsoDateTime(date, 11, 59);
}

function valueToDateTimeInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const serializedValue = value instanceof Date ? "" : String(value || "").trim();
  const useStoredClock = Boolean(serializedValue && /(?:Z|[+-]\d{2}:?\d{2})$/i.test(serializedValue));
  const year = useStoredClock ? date.getUTCFullYear() : date.getFullYear();
  const month = String((useStoredClock ? date.getUTCMonth() : date.getMonth()) + 1).padStart(2, "0");
  const day = String(useStoredClock ? date.getUTCDate() : date.getDate()).padStart(2, "0");
  const hours = String(useStoredClock ? date.getUTCHours() : date.getHours()).padStart(2, "0");
  const minutes = String(useStoredClock ? date.getUTCMinutes() : date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function sanitizeVestaTimeEntry(value) {
  return String(value || "").replace(/\D/g, "").slice(0, 4);
}

function normalizeVestaTimeInput(value) {
  const digits = sanitizeVestaTimeEntry(value);
  if (!digits) return "";
  if (digits.length !== 4) return "";
  const hours = Number(digits.slice(0, 2));
  const minutes = Number(digits.slice(2, 4));
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return "";
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function VestaTimeInput({ value = "", onChange, disabled = false, required = false, ariaLabel = "Ora" }) {
  const [editing, setEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(() => sanitizeVestaTimeEntry(value));

  useEffect(() => {
    if (!editing) setDraftValue(sanitizeVestaTimeEntry(value));
  }, [editing, value]);

  function beginEditing() {
    setEditing(true);
    setDraftValue(sanitizeVestaTimeEntry(value));
  }

  function commitDraft() {
    const formatted = normalizeVestaTimeInput(draftValue);
    setEditing(false);
    setDraftValue(sanitizeVestaTimeEntry(formatted));
    if (String(formatted) !== String(value || "")) onChange?.(formatted);
  }

  return (
    <input
      className="vesta_time_text_input"
      inputMode="numeric"
      autoComplete="off"
      value={editing ? draftValue : (normalizeVestaTimeInput(value) || "")}
      placeholder="hh:mm"
      maxLength={4}
      disabled={disabled}
      required={required}
      aria-label={ariaLabel}
      onFocus={beginEditing}
      onChange={(event) => setDraftValue(sanitizeVestaTimeEntry(event.target.value))}
      onBlur={commitDraft}
    />
  );
}

function valueToDateInput(value) {
  return formatDateForInput(value);
}

function formatDateForInput(value) {
  return formatVestaDate(value);
}

function parseFlexibleDate(value) {
  return parseVestaDate(value);
}

function normalizeDateFieldValue(value) {
  return normalizeVestaDateInput(value);
}

function toIsoDateFromInput(value) {
  return toIsoVestaDate(value);
}

function dateAndTimeToDateTimeFromInput(value, timeValue, fallbackTime) {
  const isoDate = toIsoDateFromInput(value);
  if (!isoDate) return "";
  const normalizedTime = normalizeVestaTimeInput(timeValue) || fallbackTime;
  return `${isoDate}T${normalizedTime}:00`;
}

function toCheckInDateTimeFromInput(value, timeValue = "12:00") {
  return dateAndTimeToDateTimeFromInput(value, timeValue, "12:00");
}

function toCheckOutDateTimeFromInput(value, timeValue = "11:59") {
  return dateAndTimeToDateTimeFromInput(value, timeValue, "11:59");
}

function formatRangeMonth(date) {
  return `${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

function formatMoneyFieldValue(value) {
  const raw = String(value ?? "").trim().replace(/\s/g, "").replace(/,/g, ".");
  if (!raw) return "0,00";
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return "0,00";
  return parsed.toFixed(2).replace(".", ",");
}

function normalizeMoneyToNumber(value) {
  const parsed = Number(String(value ?? "0").trim().replace(/\s/g, "").replace(/,/g, "."));
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCreditCardNumber(value) {
  const text = String(value || "").trim();
  if (text.includes("*")) return text.replace(/\s+/g, " ");
  return text
    .replace(/\D/g, "")
    .slice(0, 19)
    .replace(/(.{4})/g, "$1 ")
    .trim();
}

function normalizeMonthFieldValue(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const iso = raw.match(/^(\d{4})-(\d{2})$/);
  if (iso) return `${iso[2]}/${iso[1]}`;

  const digits = raw.replace(/\D/g, "");
  if (digits.length !== 4 && digits.length !== 6) return raw;

  const month = Number(digits.slice(0, 2));
  if (month < 1 || month > 12) return raw;

  const currentCentury = Math.floor(new Date().getFullYear() / 100) * 100;
  const yearPart = digits.slice(2);
  const year = yearPart.length === 2 ? currentCentury + Number(yearPart) : Number(yearPart);
  return `${String(month).padStart(2, "0")}/${String(year)}`;
}

function toMonthInputValue(value) {
  const normalized = normalizeMonthFieldValue(value);
  const match = normalized.match(/^(\d{2})\/(\d{4})$/);
  return match ? `${match[2]}-${match[1]}` : "";
}

function normalizeStatus(status) {
  return String(status || "CONFIRMED").trim().toUpperCase();
}

function isCancelledStatus(status) {
  return normalizeStatus(status) === "CANCELLED";
}

function getBookingName(item) {
  const raw = String(item.guest_name || "Prenotazione").trim();
  if (raw.length <= 22) return raw;
  return `${raw.slice(0, 20)}…`;
}

function money(value) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(value || 0));
}

function getBookingId(item) {
  return String(item?._id || item?.id || "");
}

function normalizeBookingPayments(source = [], legacyDepositAmount = 0, legacyPaymentMethod = "", preserveEmptyRows = false) {
  const rows = Array.isArray(source) ? source : [];
  const mapped = rows.map((payment) => {
    const paymentTypeRaw = cleanGuestText(payment?.paymentType || payment?.type).toUpperCase();
    const paymentType = ["ADVANCE", "DEPOSIT", "PAYMENT", "BALANCE"].includes(paymentTypeRaw)
      ? paymentTypeRaw
      : payment?.isDeposit === true ? "ADVANCE" : "PAYMENT";
    return {
      paymentMethod: cleanGuestText(payment?.paymentMethod || payment?.condition || payment?.method),
      paymentType,
      notes: preserveEmptyRows ? String(payment?.notes ?? payment?.note ?? "") : cleanGuestText(payment?.notes || payment?.note),
      amount: Math.max(0, normalizeMoneyToNumber(payment?.amount)),
      isDeposit: paymentType === "ADVANCE",
      advanceDocument: {
        documentId: cleanGuestText(payment?.advanceDocument?.documentId),
        documentType: cleanGuestText(payment?.advanceDocument?.documentType).toUpperCase() || "INVOICE",
        numberingMode: cleanGuestText(payment?.advanceDocument?.numberingMode).toUpperCase() === "MANUAL" ? "MANUAL" : "AUTO",
        documentNumber: cleanGuestText(payment?.advanceDocument?.documentNumber),
        emissionDate: cleanGuestText(payment?.advanceDocument?.emissionDate),
        note: cleanGuestText(payment?.advanceDocument?.note),
        orientation: cleanGuestText(payment?.advanceDocument?.orientation).toUpperCase() === "LANDSCAPE" ? "LANDSCAPE" : "PORTRAIT",
        issued: payment?.advanceDocument?.issued === true && Boolean(cleanGuestText(payment?.advanceDocument?.documentId)),
        reversalDocument: {
          documentId: cleanGuestText(payment?.advanceDocument?.reversalDocument?.documentId),
          documentType: cleanGuestText(payment?.advanceDocument?.reversalDocument?.documentType).toUpperCase() || "CREDIT_NOTE",
          numberingMode: cleanGuestText(payment?.advanceDocument?.reversalDocument?.numberingMode).toUpperCase() === "MANUAL" ? "MANUAL" : "AUTO",
          documentNumber: cleanGuestText(payment?.advanceDocument?.reversalDocument?.documentNumber),
          emissionDate: cleanGuestText(payment?.advanceDocument?.reversalDocument?.emissionDate),
          note: cleanGuestText(payment?.advanceDocument?.reversalDocument?.note),
          orientation: cleanGuestText(payment?.advanceDocument?.reversalDocument?.orientation).toUpperCase() === "LANDSCAPE" ? "LANDSCAPE" : "PORTRAIT",
          issued: payment?.advanceDocument?.issued === true
            && Boolean(cleanGuestText(payment?.advanceDocument?.documentId))
            && payment?.advanceDocument?.reversalDocument?.issued === true
            && Boolean(cleanGuestText(payment?.advanceDocument?.reversalDocument?.documentId)),
        },
      },
    };
  });
  const normalized = preserveEmptyRows
    ? mapped
    : mapped.filter((payment) => payment.paymentMethod || payment.notes || payment.amount > 0 || payment.paymentType !== "PAYMENT");

  if (normalized.length) return normalized;
  const legacyDeposit = Math.max(0, normalizeMoneyToNumber(legacyDepositAmount));
  if (legacyDeposit <= 0) return [];
  return [{
    ...EMPTY_BOOKING_PAYMENT,
    paymentMethod: cleanGuestText(legacyPaymentMethod) || "Non specificata",
    paymentType: "ADVANCE",
    amount: legacyDeposit,
    isDeposit: true,
  }];
}

function paymentMethodSelectOptions(items = [], currentValue = "") {
  const configured = catalogSelectOptions(items, currentValue);
  if (configured.length) return configured;
  const current = cleanGuestText(currentValue);
  const options = PAYMENT_METHOD_FALLBACK_OPTIONS.map((label) => ({ value: label, label }));
  if (current && !options.some((option) => option.value === current)) options.push({ value: current, label: current });
  return options;
}

function bookingToDraft(booking) {
  if (!booking) return { ...EMPTY_BOOKING };
  return {
    _id: booking._id || booking.id || "",
    guest_name: booking.guest_name || "",
    guest_email: booking.guest_email || "",
    guest_phone: booking.guest_phone || "",
    room_number: booking.room_number || "",
    room_type: booking.room_type || "Camera",
    check_in: valueToDateInput(booking.check_in),
    check_in_time: valueToDateTimeInput(booking.check_in)?.slice(11, 16) || "12:00",
    check_out: valueToDateInput(booking.check_out),
    check_out_time: valueToDateTimeInput(booking.check_out)?.slice(11, 16) || "11:59",
    adults: Number(booking.adults ?? 1),
    children: Number(booking.children ?? 0),
    animals: Number(booking.animals ?? 0),
    status: normalizeStatus(booking.status),
    _originalStatus: normalizeStatus(booking.status),
    payments: normalizeBookingPayments(booking.payments, booking.deposit_amount, booking.payment_method),
    deposit_amount: Number(booking.deposit_amount ?? 0),
    document_type: booking.document_type || "",
    document_number: booking.document_number || "",
    payment_method: booking.payment_method || "",
    total_amount: Number(booking.total_amount ?? 0),
    credit_card: booking.credit_card || "",
    credit_card_circuit_id: booking.credit_card_circuit_id || "",
    credit_card_number: formatCreditCardNumber(booking.credit_card_number || ""),
    credit_card_expiry: normalizeMonthFieldValue(booking.credit_card_expiry || ""),
    notes: booking.notes || "",
    customer_id: booking.customer_id || "",
    invoice_same_as_customer: booking.invoice_same_as_customer !== false,
    invoice_customer_id: booking.invoice_customer_id || (booking.invoice_same_as_customer !== false ? booking.customer_id || "" : ""),
    invoice_name: booking.invoice_name || "",
    invoice_email: booking.invoice_email || "",
    invoice_phone: booking.invoice_phone || "",
    guests: normalizeAdditionalGuests(booking.guests),
    relatedGuests: normalizeAdditionalGuests(booking.guests),
  };
}

function getRoomGroups(bookings, rooms = []) {
  const map = new Map();
  const sourceRooms = Array.isArray(rooms) ? rooms : [];

  sourceRooms.forEach((roomItem) => {
    const type = String(roomItem.room_type || "Camera").trim() || "Camera";
    const room = String(roomItem.room_number || "").trim();
    if (!room) return;
    if (!map.has(type)) map.set(type, new Set());
    map.get(type).add(room);
  });

  return Array.from(map.entries()).map(([type, groupRooms]) => ({
    type,
    rooms: Array.from(groupRooms).sort((a, b) => a.localeCompare(b, "it", { numeric: true })),
  })).sort((a, b) => a.type.localeCompare(b.type, "it", { numeric: true }));
}

const EMPTY_CUSTOMER = {
  nazione: "Italia",
  customerType: "",
  codiceFiscale: "",
  partitaIva: "",
  codiceSdi: "",
  cognome: "",
  nome: "",
  dataNascita: "",
  luogoNascita: "",
  comuneNascita: "",
  provinciaNascita: "",
  statoNascita: "Italia",
  cittadinanza: "Italia",
  sesso: "",
  ragioneSociale: "",
  indirizzo: "",
  civico: "",
  citta: "",
  provincia: "",
  cap: "",
  contacts: [],
  documentoTipo: "",
  documentoNumero: "",
  luogoRilascioDocumento: "",
  paeseRilascioDocumento: "",
  dataRilascioDocumento: "",
  dataScadenzaDocumento: "",
  enteRilascioDocumento: "",
  documentoPrincipale: true,
  linguaPreferita: "",
  preferredRoomTypeId: "",
  pianoPreferito: "",
  preferredBedTypeId: "",
  allergie: "",
  intolleranze: "",
  esigenzeSpeciali: "",
  preferenzaArrivo: "",
  customerTags: "",
  privacyConsent: false,
  privacyConsentDate: "",
  marketingConsent: false,
  marketingConsentDate: "",
  profilingConsent: false,
  profilingConsentDate: "",
  dataProcessingNotes: "",
  isVip: false,
  isBlocked: false,
  blockedReason: "",
  internalAlert: "",
  touristTaxExemptionDefault: false,
  touristTaxExemptionReason: "",
  touristTaxExemptionDocument: "",
  customerStatus: "ACTIVE",
  mergedIntoCustomerId: "",
  note: "",
};

function catalogSelectOptions(items = [], currentValue = "") {
  const current = String(currentValue || "").trim();
  const options = (items || [])
    .filter((item) => item.active !== false)
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0) || String(a.description || "").localeCompare(String(b.description || ""), "it"))
    .map((item) => ({ value: item.description, label: item.description }));
  if (current && !options.some((option) => String(option.value) === current)) {
    options.push({ value: current, label: current });
  }
  return options;
}

function catalogIdSelectOptions(items = [], currentValue = "") {
  const current = String(currentValue || "").trim();
  const options = (items || [])
    .filter((item) => item.active !== false)
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0) || String(a.description || "").localeCompare(String(b.description || ""), "it"))
    .map((item) => ({ value: String(item._id), label: item.description }));
  if (current && !options.some((option) => String(option.value) === current)) {
    options.push({ value: current, label: current });
  }
  return options;
}

function selectOverlayFieldValue(event) {
  const target = event.target;
  if (!target || typeof target.select !== "function") return;
  if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") return;
  const type = String(target.getAttribute("type") || "text").toLowerCase();
  if (["checkbox", "radio", "button", "submit", "reset", "hidden", "date", "month", "time", "file"].includes(type)) return;
  window.setTimeout(() => target.select(), 0);
}

function FieldWithAction({ label, value, required, onChange, onAction, actionIcon, placeholder }) {
  const isAddAction = actionIcon === "+";
  return (
    <label className="overlay_field overlay_field_action">
      <OverlayFieldLabel required={required}>{label}</OverlayFieldLabel>
      <div className="overlay_input_action_wrap">
        <input value={value || ""} placeholder={placeholder || ""} onChange={(e) => onChange(e.target.value)} />
        <button
          type="button"
          className={`overlay_embedded_action_button${isAddAction ? " overlay_add_action_button" : " overlay_edit_action_button"}`}
          onClick={onAction}
          aria-label={isAddAction ? "Nuova anagrafica" : "Modifica anagrafica"}
          tabIndex={-1}
        >
          {isAddAction ? (
            <svg viewBox="0 0 24 24" aria-hidden="true" className="overlay_action_icon overlay_add_icon">
              <path d="M12 5v14M5 12h14" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" aria-hidden="true" className="overlay_action_icon overlay_edit_icon">
              <path d="M5 19.25l3.55-.8L18.1 8.9a2.05 2.05 0 0 0 0-2.9l-.1-.1a2.05 2.05 0 0 0-2.9 0l-9.55 9.55-.55 3.8z" />
              <path d="M13.75 7.25l3 3" />
              <path d="M5 19.25h4.25" />
            </svg>
          )}
        </button>
      </div>
    </label>
  );
}

function CustomerAutocompleteField({ label, value, customerId, customers, onChange, onSelect, onAction, disabled = false, required = true }) {
  const [open, setOpen] = useState(false);
  const [dropdownReady, setDropdownReady] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef(null);
  const dropdownRef = useRef(null);
  const query = disabled ? "" : cleanGuestText(value).toLocaleLowerCase("it-IT");
  const matches = query
    ? (customers || [])
      .filter((customer) => customerDisplayName(customer).toLocaleLowerCase("it-IT").includes(query))
      .slice(0, 5)
    : [];
  const hasSelectedCustomer = Boolean(cleanGuestText(customerId));
  const isAddAction = !hasSelectedCustomer;

  useEffect(() => {
    if (!open || matches.length === 0) {
      setActiveIndex(-1);
      return;
    }
    setActiveIndex(0);
  }, [open, query, matches.length]);

  useEffect(() => {
    if (!open) return undefined;

    const updateDropdownPosition = () => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect || !dropdownRef.current) return;
      const top = rect.bottom + 4;
      dropdownRef.current.style.setProperty("--booking-customer-autocomplete-top", `${top}px`);
      dropdownRef.current.style.setProperty("--booking-customer-autocomplete-left", `${rect.left}px`);
      dropdownRef.current.style.setProperty("--booking-customer-autocomplete-width", `${rect.width}px`);
      setDropdownReady(true);
    };

    const closeDropdown = () => {
      setOpen(false);
      setDropdownReady(false);
      setActiveIndex(-1);
    };
    const closeOnPointerDown = (event) => {
      if (wrapRef.current?.contains(event.target) || dropdownRef.current?.contains(event.target)) return;
      closeDropdown();
    };
    const closeOnOverlayScroll = (event) => {
      if (dropdownRef.current?.contains(event?.target)) return;
      closeDropdown();
    };
    const field = wrapRef.current;
    const dropdown = dropdownRef.current;
    const routeDropdownWheel = (event) => {
      if (!dropdown) return;
      event.preventDefault();
      event.stopPropagation();
      const wheelUnit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? Math.max(dropdown.clientHeight, 1) : 1;
      dropdown.scrollTop += event.deltaY * wheelUnit;
    };

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", closeOnOverlayScroll, true);
    document.addEventListener("vesta:close-dropdowns", closeDropdown);
    document.addEventListener("pointerdown", closeOnPointerDown, true);
    field?.addEventListener("wheel", routeDropdownWheel, { passive: false });
    dropdown?.addEventListener("wheel", routeDropdownWheel, { passive: false });
    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", closeOnOverlayScroll, true);
      document.removeEventListener("vesta:close-dropdowns", closeDropdown);
      document.removeEventListener("pointerdown", closeOnPointerDown, true);
      field?.removeEventListener("wheel", routeDropdownWheel);
      dropdown?.removeEventListener("wheel", routeDropdownWheel);
    };
  }, [open, matches.length]);

  function handleValueChange(nextValue) {
    if (disabled) return;
    onChange(nextValue);
    const hasQuery = Boolean(cleanGuestText(nextValue));
    setOpen(hasQuery);
    if (!hasQuery) {
      setDropdownReady(false);
      setActiveIndex(-1);
    }
  }

  function chooseCustomer(customer) {
    if (disabled || !customer) return;
    onSelect(customer);
    setOpen(false);
    setDropdownReady(false);
    setActiveIndex(-1);
  }

  function handleKeyDown(event) {
    if (disabled || !open || matches.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (current < 0 || current >= matches.length - 1 ? 0 : current + 1));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => (current <= 0 ? matches.length - 1 : current - 1));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      chooseCustomer(matches[activeIndex >= 0 ? activeIndex : 0]);
      return;
    }

    if (event.key === "Tab") {
      chooseCustomer(matches[activeIndex >= 0 ? activeIndex : 0]);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      setDropdownReady(false);
      setActiveIndex(-1);
    }
  }

  const dropdown = open && matches.length > 0 ? createPortal(
    <div
      ref={dropdownRef}
      className={`booking_customer_autocomplete_dropdown${dropdownReady ? " is_ready" : ""}`}
      role="listbox"
      aria-label="Risultati Ragione Sociale"
    >
      {matches.map((customer, index) => {
        const id = String(customer._id || customer.id || "");
        const name = customerDisplayName(customer);
        const fiscalReference = customerFiscalReference(customer);
        const addressSummary = customerAutocompleteAddressSummary(customer);
        const isActive = index === activeIndex;
        return (
          <button
            key={id || name}
            type="button"
            className={`booking_customer_autocomplete_option${isActive ? " is_active" : ""}`}
            role="option"
            aria-selected={isActive}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => chooseCustomer(customer)}
          >
            <strong>{name}</strong>
            {fiscalReference && <span>{fiscalReference}</span>}
            {addressSummary && <span>{addressSummary}</span>}
          </button>
        );
      })}
    </div>,
    document.body
  ) : null;

  return (
    <label className={`overlay_field overlay_field_action booking_customer_autocomplete_field${disabled ? " is_disabled" : ""}`}>
      <OverlayFieldLabel required={required}>{label}</OverlayFieldLabel>
      <div ref={wrapRef} className="overlay_input_action_wrap">
        <input
          value={value || ""}
          autoComplete="off"
          disabled={disabled}
          aria-autocomplete="list"
          aria-expanded={open && matches.length > 0}
          onChange={(event) => handleValueChange(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className={`overlay_embedded_action_button${isAddAction ? " overlay_add_action_button" : " overlay_edit_action_button"}`}
          onClick={onAction}
          aria-label={isAddAction ? "Nuova anagrafica" : "Modifica anagrafica"}
          disabled={disabled}
          tabIndex={-1}
        >
          {isAddAction ? (
            <svg viewBox="0 0 24 24" aria-hidden="true" className="overlay_action_icon overlay_add_icon">
              <path d="M12 5v14M5 12h14" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" aria-hidden="true" className="overlay_action_icon overlay_customer_edit_icon">
              <path d="M5 19.25l3.55-.8L18.1 8.9a2.05 2.05 0 0 0 0-2.9l-.1-.1a2.05 2.05 0 0 0-2.9 0l-9.55 9.55-.55 3.8z" />
              <path d="M13.75 7.25l3 3" />
              <path d="M5 19.25h4.25" />
            </svg>
          )}
        </button>
      </div>
      {dropdown}
    </label>
  );
}

function OverlaySelect({ label, value, required, placeholder, options, onChange, error = "", fieldKey = "", disabled = false }) {
  const [open, setOpen] = useState(false);
  const [dropdownReady, setDropdownReady] = useState(false);
  const wrapRef = useRef(null);
  const dropdownRef = useRef(null);
  const typeBufferRef = useRef("");
  const typeBufferTimeRef = useRef(0);
  const selected = (options || []).find((item) => String(item.value) === String(value));
  const placeholderText = String(placeholder || "").toUpperCase();

  useEffect(() => {
    if (disabled && open) {
      setOpen(false);
      return undefined;
    }
    if (!open || disabled) return undefined;

    const updateDropdownPosition = () => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect || !dropdownRef.current) return;
      const top = rect.bottom + 3;
      dropdownRef.current.style.setProperty("--overlay-select-top", `${top}px`);
      dropdownRef.current.style.setProperty("--overlay-select-left", `${rect.left}px`);
      dropdownRef.current.style.setProperty("--overlay-select-width", `${rect.width}px`);
      const dropdownWidth = dropdownRef.current.getBoundingClientRect().width;
      const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - dropdownWidth - 8));
      dropdownRef.current.style.setProperty("--overlay-select-left", `${left}px`);
      setDropdownReady(true);
    };

    const closeDropdown = () => setOpen(false);
    const close = (event) => {
      const target = event.target;
      if (!wrapRef.current?.contains(target) && !dropdownRef.current?.contains(target)) setOpen(false);
    };
    const closeOnOverlayScroll = (event) => {
      if (dropdownRef.current?.contains(event?.target)) return;
      setOpen(false);
    };
    const trigger = wrapRef.current?.querySelector(".overlay_select_button");
    const dropdown = dropdownRef.current;
    const routeDropdownWheel = (event) => {
      if (!dropdown) return;
      event.preventDefault();
      event.stopPropagation();
      const wheelUnit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? Math.max(dropdown.clientHeight, 1) : 1;
      dropdown.scrollTop += event.deltaY * wheelUnit;
    };

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", closeOnOverlayScroll, true);
    document.addEventListener("vesta:close-dropdowns", closeDropdown);
    document.addEventListener("pointerdown", close, true);
    trigger?.addEventListener("wheel", routeDropdownWheel, { passive: false });
    dropdown?.addEventListener("wheel", routeDropdownWheel, { passive: false });
    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", closeOnOverlayScroll, true);
      document.removeEventListener("vesta:close-dropdowns", closeDropdown);
      document.removeEventListener("pointerdown", close, true);
      trigger?.removeEventListener("wheel", routeDropdownWheel);
      dropdown?.removeEventListener("wheel", routeDropdownWheel);
    };
  }, [open, disabled]);

  function chooseOption(item) {
    if (disabled) return;
    onChange(item.value);
    setOpen(false);
  }

  function handleTypeAhead(event) {
    if (disabled || event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
    const targetIsOption = event.target?.closest?.(".overlay_select_option:not(.overlay_select_option_default)");
    if (targetIsOption && (event.key === "Enter" || event.key === " ")) return;
    if (event.key === "Tab") {
      setOpen(false);
      return;
    }
    const now = Date.now();
    const typeBufferActive = now - typeBufferTimeRef.current <= 850 && typeBufferRef.current.length > 0;
    if (event.key === "Enter" || (event.key === " " && !open && !typeBufferActive)) {
      event.preventDefault();
      setDropdownReady(false);
      setOpen((current) => !current);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setDropdownReady(false);
      setOpen(true);
      return;
    }
    if (event.key !== "Backspace" && event.key.length !== 1) return;

    const expired = now - typeBufferTimeRef.current > 850;
    typeBufferTimeRef.current = now;
    typeBufferRef.current = event.key === "Backspace"
      ? (expired ? "" : typeBufferRef.current.slice(0, -1))
      : `${expired ? "" : typeBufferRef.current}${event.key}`;

    if (event.key === " ") event.preventDefault();
    const typed = typeBufferRef.current.trim().toLocaleLowerCase("it");
    if (!typed) return;
    const match = (options || []).find((item) => String(item.label || "").trim().toLocaleLowerCase("it").startsWith(typed));
    if (!match) return;
    event.preventDefault();
    onChange(match.value);
  }

  const dropdown = open && !disabled ? createPortal(
    <div ref={dropdownRef} className={`overlay_select_dropdown overlay_select_dropdown_portal${dropdownReady ? " is_ready" : ""}`} role="listbox" onKeyDown={handleTypeAhead}>
      <div className="overlay_select_option overlay_select_option_default" aria-disabled="true">{placeholderText}</div>
      {(options || []).map((item) => (
        <button
          key={item.value}
          type="button"
          className="overlay_select_option"
          role="option"
          aria-selected={String(item.value) === String(value)}
          onClick={() => chooseOption(item)}
        >
          {item.label}
        </button>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <label className={overlayFieldClass(error, "overlay_select_field")} ref={wrapRef} data-field-key={fieldKey || undefined}>
      <OverlayFieldLabel required={required}>{label}</OverlayFieldLabel>
      <button
        type="button"
        className={`overlay_select_button${selected ? "" : " is_placeholder"}`}
        onClick={() => {
          if (disabled) return;
          setDropdownReady(false);
          setOpen((current) => !current);
        }}
        onKeyDown={handleTypeAhead}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-selected-value={String(value ?? "")}
      >
        <span>{selected?.label || placeholderText}</span>
        <span className="overlay_select_arrow" aria-hidden="true">
          <svg className="overlay_select_arrow_icon" viewBox="0 0 24 24" focusable="false">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>
      {dropdown}
      <OverlayFieldError message={error} />
    </label>
  );
}

function CurrencyField({ label, value, onChange, className = "", disabled = false, error = "", preserveTyping = false }) {
  const formattedValue = value === undefined || value === null || value === "" || typeof value === "number"
    ? formatMoneyFieldValue(value)
    : String(value).replace(".", ",");
  const [editingValue, setEditingValue] = useState(formattedValue);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) setEditingValue(formattedValue);
  }, [editing, formattedValue]);

  const displayValue = preserveTyping && editing ? editingValue : formattedValue;

  return (
    <label className={overlayFieldClass(error, `overlay_money_field${className ? ` ${className}` : ""}`)}>
      <span>{label}</span>
      <div className="overlay_money_input_wrap">
        <span className="overlay_money_symbol" aria-hidden="true">€</span>
        <input
          inputMode="decimal"
          value={displayValue}
          disabled={disabled}
          onFocus={(event) => {
            if (!preserveTyping || disabled) return;
            const input = event.currentTarget;
            setEditing(true);
            setEditingValue(formattedValue);
            window.requestAnimationFrame(() => input.select());
          }}
          onChange={(event) => {
            if (disabled) return;
            const nextValue = event.target.value.replace(/[^0-9,.]/g, "");
            if (preserveTyping) setEditingValue(nextValue);
            onChange(nextValue);
          }}
          onBlur={(event) => {
            if (disabled) return;
            const formatted = formatMoneyFieldValue(event.target.value);
            if (preserveTyping) {
              setEditing(false);
              setEditingValue(formatted);
            }
            onChange(formatted);
          }}
        />
      </div>
      <OverlayFieldError message={error} />
    </label>
  );
}

function DateField({ label, value, required, onChange, error = "", fieldKey = "" }) {
  return (
    <label className={overlayFieldClass(error, "overlay_field_action")} data-field-key={fieldKey || undefined}>
      <OverlayFieldLabel required={required}>{label}</OverlayFieldLabel>
      <VestaDateInput value={value} required={required} onChange={onChange} />
      <OverlayFieldError message={error} />
    </label>
  );
}

function MonthField({ label, value, onChange }) {
  const pickerRef = useRef(null);
  const pickerValue = toMonthInputValue(value);
  return (
    <label className="overlay_field overlay_field_action">
      <span>{label}</span>
      <div className="overlay_input_action_wrap">
        <input value={value || ""} placeholder="mm/aaaa" maxLength="7" onChange={(e) => onChange(e.target.value.replace(/[^0-9/]/g, ""))} onBlur={(e) => onChange(normalizeMonthFieldValue(e.target.value))} />
        <input ref={pickerRef} className="overlay_hidden_date_picker" type="month" value={pickerValue} onChange={(e) => onChange(normalizeMonthFieldValue(e.target.value))} tabIndex="-1" aria-hidden="true" />
        <button type="button" className="overlay_embedded_action_button overlay_calendar_action_button" onClick={() => pickerRef.current?.showPicker?.() || pickerRef.current?.click()} aria-label="Apri calendario mese" tabIndex={-1}>
          <svg viewBox="0 0 24 24" aria-hidden="true" className="overlay_calendar_icon">
            <rect x="3.75" y="5.25" width="16.5" height="15" rx="3" />
            <path d="M7.5 3.5v3.5M16.5 3.5v3.5M4.25 9.25h15.5" />
            <path d="M8 13.5h8M8 17h5" />
          </svg>
        </button>
      </div>
    </label>
  );
}

function AdditionalGuestsEditor({ guests, customers, primaryCustomerId, referenceDate, onChange, onCustomerAction }) {
  const rows = Array.isArray(guests) ? guests : [];

  function normalizedRows() {
    return rows.map((guest) => ({ ...EMPTY_ADDITIONAL_GUEST, ...guest }));
  }

  function commit(nextRows) {
    onChange(nextRows);
  }

  useEffect(() => {
    let changed = false;
    const next = rows.map((guest) => {
      const normalized = { ...EMPTY_ADDITIONAL_GUEST, ...guest };
      const role = normalizeGuestRole(normalized.relationshipType || normalized.guestRole);
      if (role === "ANIMAL") return normalized;
      const customerId = cleanGuestText(normalized.customer_id || normalized.customerId);
      if (!customerId) return normalized;
      const customer = findCustomerById(customers, customerId);
      if (!customer) return normalized;
      const minorByBirthDate = isCustomerChildAtDate(customer, referenceDate);
      if (minorByBirthDate === null || normalized.isMinor === minorByBirthDate) return normalized;
      changed = true;
      return { ...normalized, isMinor: minorByBirthDate };
    });
    if (changed) onChange(next);
  }, [rows, customers, referenceDate, onChange]);

  function updateGuest(index, field, value) {
    const next = normalizedRows().map((guest, rowIndex) => {
      if (rowIndex !== index) return guest;
      const updated = { ...guest, [field]: value };
      if (field === "customerName") updated.customer_id = "";
      if (field === "included") updated.included = value === true;
      if (field === "relationshipType") {
        const nextRole = normalizeGuestRole(value);
        const previousRole = normalizeGuestRole(guest.relationshipType || guest.guestRole);
        if (nextRole === "ANIMAL") {
          updated.customer_id = "";
          updated.customerName = "";
        } else if (previousRole === "ANIMAL") {
          updated.included = true;
        }
      }
      return updated;
    });
    commit(next);
  }

  function selectGuestCustomer(index, customer) {
    const customerId = cleanGuestText(customer?._id || customer?.id);
    if (!customerId || customerId === cleanGuestText(primaryCustomerId)) return;
    const duplicate = normalizedRows().some((guest, rowIndex) => rowIndex !== index && cleanGuestText(guest.customer_id) === customerId);
    if (duplicate) return;
    const minorByBirthDate = isCustomerChildAtDate(customer || {}, referenceDate);
    const next = normalizedRows().map((guest, rowIndex) => rowIndex !== index ? guest : {
      ...guest,
      customer_id: customerId,
      customerName: customerDisplayName(customer || {}),
      isMinor: minorByBirthDate === null ? guest.isMinor === true : minorByBirthDate,
    });
    commit(next);
  }

  function removeGuest(index) {
    commit(normalizedRows().filter((_, rowIndex) => rowIndex !== index));
  }

  function addGuest() {
    commit([...normalizedRows(), { ...EMPTY_ADDITIONAL_GUEST, included: true }]);
  }

  return (
    <div className="booking_related_guests_editor">
      <div className="booking_related_guests_toolbar">
        <button type="button" className="booking_related_guests_add_button overlay_list_add_button" onClick={addGuest} aria-label="Aggiungi ospite correlato" title="Aggiungi ospite correlato" tabIndex={-1}>
          <svg viewBox="0 0 24 24" aria-hidden="true" className="overlay_action_icon overlay_add_icon">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
        <p>Gli ospiti collegati all'anagrafica restano memorizzati. Il check IN HOUSE indica chi partecipa a questa prenotazione.</p>
      </div>

      {rows.length === 0 ? (
        <div className="booking_related_guests_empty">Nessun ospite correlato collegato a questa anagrafica.</div>
      ) : (
        <div className="booking_related_guests_list">
          {rows.map((guest, index) => {
            const currentId = cleanGuestText(guest.customer_id);
            const guestRole = normalizeGuestRole(guest.relationshipType || guest.guestRole);
            const isAnimal = guestRole === "ANIMAL";
            const usedIds = new Set(normalizedRows().map((item, rowIndex) => rowIndex === index ? "" : cleanGuestText(item.customer_id)).filter(Boolean));
            const selectableCustomers = (customers || []).filter((customer) => {
              const id = cleanGuestText(customer?._id || customer?.id);
              return id && id !== cleanGuestText(primaryCustomerId) && (id === currentId || !usedIds.has(id));
            });
            return (
              <div className={`booking_related_guest_row${isAnimal ? " is_animal" : ""}`} key={`related-guest-${index}`}>
                <CustomerAutocompleteField
                  label="Ragione sociale"
                  value={guest.customerName || customerDisplayName(findCustomerById(customers, currentId) || {})}
                  customerId={currentId}
                  customers={selectableCustomers}
                  onChange={(value) => updateGuest(index, "customerName", value)}
                  onSelect={(customer) => selectGuestCustomer(index, customer)}
                  onAction={() => onCustomerAction?.({ type: "guest", index })}
                  disabled={isAnimal}
                  required={!isAnimal}
                />
                <OverlaySelect
                  label="Tipo ospite"
                  value={guestRole}
                  placeholder="Seleziona relazione"
                  options={RELATED_GUEST_TYPE_OPTIONS}
                  onChange={(value) => updateGuest(index, "relationshipType", value)}
                />
                <label className="overlay_field overlay_check_field booking_related_guest_presence">
                  <span>In House</span>
                  <input type="checkbox" checked={guest.included === true} onChange={(event) => updateGuest(index, "included", event.target.checked)} />
                </label>
                <button type="button" className="overlay_contact_remove_button booking_related_guest_remove" onClick={() => removeGuest(index)} aria-label="Scollega ospite correlato" title="Scollega ospite correlato" tabIndex={-1}>×</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function currentHotelDateIso() {
  const parts = new Intl.DateTimeFormat("it-IT", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const read = (type) => parts.find((part) => part.type === type)?.value || "";
  return `${read("year")}-${read("month")}-${read("day")}`;
}

function advanceReportFormatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("it-IT");
}

function advanceReportFormatMoney(value) {
  return `${Number(value || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function advanceReportStayNights(checkIn, checkOut) {
  const startValue = valueToDateTimeInput(checkIn);
  const endValue = valueToDateTimeInput(checkOut);
  if (!startValue || !endValue || new Date(endValue) <= new Date(startValue)) return 0;
  const startDay = Date.parse(`${startValue.slice(0, 10)}T00:00:00Z`);
  const endDay = Date.parse(`${endValue.slice(0, 10)}T00:00:00Z`);
  return Math.max(1, Math.round((endDay - startDay) / 86400000));
}

function advanceReportPaymentSummary(booking = {}) {
  const payments = Array.isArray(booking.payments) ? booking.payments : [];
  const methods = [...new Set([
    ...payments.map((item) => String(item?.paymentMethod || "").trim()),
    String(booking.payment_method || "").trim(),
  ].filter(Boolean))];
  return methods.length ? methods.join(" · ") : "Non specificato";
}

function advanceReportDocumentLabel(documentType) {
  if (documentType === "CREDIT_NOTE") return "Nota di Credito";
  if (documentType === "RECEIPT_REVERSAL") return "Storno Ricevuta";
  return documentType === "RECEIPT" ? "Ricevuta" : "Fattura";
}

function advanceReportFiscalSection(documentType) {
  if (documentType === "CREDIT_NOTE") return "NC";
  if (documentType === "RECEIPT_REVERSAL") return "SR";
  return documentType === "RECEIPT" ? "RC" : "FT";
}

function handleAdvanceCompanyLogoError(event) {
  event.currentTarget.closest(".booking_fiscal_company_logo_box")?.classList.add("is_vesta_fallback");
  if (event.currentTarget.src !== vestaLogo) event.currentTarget.src = vestaLogo;
}

function AdvancePaymentFiscalReportPage({ booking = {}, payment, documentKind = "advance", printMode = false }) {
  const primaryDocument = payment?.advanceDocument || {};
  const previewDocument = documentKind === "reversal" ? (primaryDocument.reversalDocument || {}) : primaryDocument;
  const documentType = previewDocument.documentType || (documentKind === "reversal" ? "CREDIT_NOTE" : "INVOICE");
  const documentLabel = advanceReportDocumentLabel(documentType);
  const fiscalSection = advanceReportFiscalSection(documentType);
  const isInvoice = ["INVOICE", "CREDIT_NOTE"].includes(documentType);
  const orientation = previewDocument.orientation === "LANDSCAPE" ? "landscape" : "portrait";
  const companyProfile = booking.company_profile || {};
  const companyName = companyProfile.description || booking.company_name || getCompanyDisplayName();
  const companyLogoUrl = String(booking.company_logo_url || "").trim();
  const legalSite = booking.legal_site || {};
  const customerName = booking.invoice_name || booking.guest_name || "-";
  const guestCustomerName = booking.guest_customer_name || booking.guest_name || "-";
  const customerAddress = booking.invoice_address || booking.customer_address || "-";
  const customerPhone = booking.invoice_phone || booking.customer_phone || "-";
  const customerEmail = booking.invoice_email || booking.customer_email || "-";
  const customerFiscalCode = booking.invoice_fiscal_code || booking.customer_fiscal_code || "-";
  const customerVatNumber = booking.invoice_vat_number || booking.customer_vat_number || "-";
  const customerSdiCode = booking.invoice_sdi_code || "-";
  const nights = advanceReportStayNights(booking.check_in, booking.check_out);
  const paymentMethods = advanceReportPaymentSummary(booking);
  const amount = Math.max(0, Number(payment?.amount || 0));
  const legalAddress = [legalSite.address, legalSite.streetNumber].filter(Boolean).join(" ").trim() || "-";
  const legalCity = [legalSite.cap, legalSite.city].filter(Boolean).join(" ").trim();
  const legalProvince = String(legalSite.province || "").trim();
  const legalLocation = [legalCity, legalProvince ? `(${legalProvince})` : ""].filter(Boolean).join(" ") || "-";
  const row = [
    "Acconto",
    advanceReportFormatMoney(amount),
    "1",
    advanceReportFormatMoney(amount),
    "0",
    advanceReportFormatMoney(amount),
    "-",
    advanceReportFormatMoney(0),
    advanceReportFormatMoney(amount),
  ];
  const customerFields = [
    ["Intestatario Fattura", customerName, "is_wide is_primary_value"],
    ["Indirizzo", customerAddress, "is_wide"],
    ["Codice Fiscale", customerFiscalCode, ""],
    ["Partita IVA", customerVatNumber, ""],
    ["Codice SDI", customerSdiCode, ""],
    ["Telefono", customerPhone, ""],
    ["Mail / PEC", customerEmail, "is_wide"],
  ];
  const bookingFields = [
    ["Ospite", guestCustomerName, "is_wide is_primary_value"],
    ["Camera", booking.room_number || "-", ""],
    ["Tipologia", booking.room_type || "-", ""],
    ["Check In", advanceReportFormatDate(booking.check_in), ""],
    ["Ora Check In", valueToDateTimeInput(booking.check_in)?.slice(11, 16) || "-", ""],
    ["Check Out", advanceReportFormatDate(booking.check_out), ""],
    ["Ora Check Out", valueToDateTimeInput(booking.check_out)?.slice(11, 16) || "-", ""],
    ["Notti", String(nights), ""],
    ["Metodo Pagamento", paymentMethods, ""],
    ["Note Documento", String(previewDocument.note || "").trim() || "-", "is_wide is_note_value"],
  ];

  return (
    <div className={`utility_page_sheet booking_fiscal_report booking_fiscal_report_v2 ${isInvoice ? "booking_invoice_report" : "booking_receipt_report"}${!printMode ? " utility_screen_sheet" : ""}${orientation === "landscape" ? " is_landscape" : ""}`}>
      <>
        <div className="booking_fiscal_report_header booking_fiscal_report_header_v2">
          <div className={`booking_fiscal_company_logo_box${companyLogoUrl ? "" : " is_vesta_fallback"}`}>
            <img src={companyLogoUrl || vestaLogo} onError={handleAdvanceCompanyLogoError} alt={companyName || "VestaMNG"} />
          </div>
          <div className="booking_fiscal_company_identity">
            <strong>{companyName || "VestaMNG"}</strong>
            <span>P. IVA: {companyProfile.vatNumber || "-"}</span>
            <span>Codice Fiscale: {companyProfile.fiscalCode || "-"}</span>
            <span>Capitale Sociale: {Number(companyProfile.shareCapital || 0) > 0 ? advanceReportFormatMoney(companyProfile.shareCapital) : "-"}</span>
            <span>REA: {companyProfile.reaCode || "-"}</span>
            <span>Codice SDI: {companyProfile.sdiCode || "-"}</span>
          </div>
          <div className="booking_fiscal_legal_site">
            <span><b>Indirizzo:</b> {legalAddress}</span>
            <span><b>Località:</b> {legalLocation}</span>
            <span><b>Telefono:</b> {legalSite.phone || "-"}</span>
            <span><b>Email:</b> {legalSite.email || "-"}</span>
            <span><b>Banca:</b> {[companyProfile.primaryBank?.bankName, companyProfile.primaryBank?.branchName].filter(Boolean).join(" - ") || "-"} · <b>IBAN:</b> {companyProfile.primaryBank?.iban || companyProfile.iban || "-"}</span>
          </div>
          <div className="booking_fiscal_header_spacer" aria-hidden="true" />
          <div className="booking_fiscal_vesta_logo_box">
            <img src={vestaLogo} alt="Vesta" />
          </div>
        </div>
        <div className="booking_fiscal_report_rule" />
      </>

      <div className="booking_fiscal_info_sections">
        <section className="booking_fiscal_info_card booking_fiscal_info_card_v2">
          <h4>Dati Cliente</h4>
          <div className="booking_fiscal_field_grid">
            {customerFields.map(([label, value, className]) => (
              <div key={label} className={className || undefined}>
                <span>{label}</span>
                <strong>{value || "-"}</strong>
              </div>
            ))}
          </div>
        </section>
        <section className="booking_fiscal_info_card booking_fiscal_info_card_v2">
          <h4>Dati Soggiorno</h4>
          <div className="booking_fiscal_field_grid">
            {bookingFields.map(([label, value, className]) => (
              <div key={label} className={className || undefined}>
                <span>{label}</span>
                <strong>{value || "-"}</strong>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="booking_fiscal_document_data_section">
        <h4>Dati Documento</h4>
        <div className="booking_fiscal_document_data_grid">
          <div><span>Tipo Documento</span><strong>{documentLabel}</strong></div>
          <div><span>Numero / Sezionale</span><strong>{previewDocument.documentNumber || "-"} / {fiscalSection}</strong></div>
          <div><span>Data</span><strong>{advanceReportFormatDate(previewDocument.emissionDate || currentHotelDateIso())}</strong></div>
        </div>
      </section>

      <div className="booking_fiscal_table_wrap">
        <table className="booking_fiscal_table booking_fiscal_table_v2">
          <thead>
            <tr>
              <th>Descrizione</th>
              <th>Prezzo</th>
              <th>Qta</th>
              <th>Imponibile</th>
              <th>% Sconto</th>
              <th>Netto</th>
              <th>% IVA</th>
              <th>Imposta</th>
              <th>Ivato</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              {row.map((cell, cellIndex) => <td key={`advance-report-cell-${cellIndex}`}>{cell}</td>)}
            </tr>
          </tbody>
        </table>
      </div>

      <div className={`booking_fiscal_bottom_zone booking_fiscal_bottom_zone_v2 ${isInvoice ? "booking_invoice_bottom_zone" : "booking_receipt_bottom_zone"}`}>
        {isInvoice ? (
          <div className="booking_invoice_vat_box">
            <div className="booking_invoice_vat_title"><span>Castelletto IVA</span></div>
            <div className="booking_invoice_vat_table">
              <div className="booking_invoice_vat_row booking_invoice_vat_header"><span>% IVA</span><span>Imponibile</span><span>Imposta</span><span>Totale Ivato</span></div>
              <div className="booking_invoice_vat_row is_unconfigured">
                <strong>-</strong>
                <strong>{advanceReportFormatMoney(amount)}</strong>
                <strong>{advanceReportFormatMoney(0)}</strong>
                <strong>{advanceReportFormatMoney(amount)}</strong>
              </div>
            </div>
          </div>
        ) : <div className="booking_fiscal_bottom_spacer" aria-hidden="true" />}
        <div className="booking_fiscal_totals_panel booking_fiscal_totals_panel_v2">
          <div><span>Totale Imponibile</span><strong>{advanceReportFormatMoney(amount)}</strong></div>
          <div><span>Totale Imposta</span><strong>{advanceReportFormatMoney(0)}</strong></div>
          <div className="booking_fiscal_document_total"><span>{["CREDIT_NOTE", "RECEIPT_REVERSAL"].includes(documentType) ? `Totale ${documentLabel}` : (isInvoice ? "Totale Fattura" : "Totale Ricevuta")}</span><strong>{advanceReportFormatMoney(amount)}</strong></div>
        </div>
      </div>

      <div className="booking_fiscal_report_footer booking_fiscal_report_footer_v2">
        <strong>Pagina 1 / 1</strong>
      </div>
    </div>
  );
}

function AdvancePaymentPreview({ booking, payment, documentKind = "advance", onClose, onPrint, onExport, onToggleOrientation }) {
  const primaryDocument = payment?.advanceDocument || {};
  const previewDocument = documentKind === "reversal" ? (primaryDocument.reversalDocument || {}) : primaryDocument;
  const orientation = previewDocument.orientation === "LANDSCAPE" ? "landscape" : "portrait";

  return (
    <UtilityFullscreenPreview
      open
      orientation={orientation}
      currentPage={0}
      pageCount={1}
      onPageChange={() => {}}
      onPrint={onPrint}
      onExport={onExport}
      onToggleOrientation={onToggleOrientation}
      onClose={onClose}
    >
      <AdvancePaymentFiscalReportPage booking={booking} payment={payment} documentKind={documentKind} />
    </UtilityFullscreenPreview>
  );
}
function BookingFiscalActionIconButton({ action, onClick, orientation = "PORTRAIT", title = "", disabled = false }) {
  const landscape = String(orientation || "PORTRAIT").toUpperCase() === "LANDSCAPE";
  const activeOrientationLabel = landscape ? "Foglio orizzontale" : "Foglio verticale";
  const labels = {
    print: "Ristampa",
    export: "Esporta / Download",
    preview: "Anteprima di stampa",
    orientation: activeOrientationLabel,
  };
  const label = title || labels[action] || "Azione";
  const buttonClass = [
    "utility_icon_action_button",
    action === "orientation" ? "utility_orientation_button" : "",
  ].filter(Boolean).join(" ");

  let icon = null;
  if (action === "orientation") icon = <svg className={`utility_orientation_icon ${landscape ? "is_landscape" : "is_portrait"}`} viewBox="0 0 24 24" aria-hidden="true"><rect x={landscape ? "3.5" : "7"} y={landscape ? "7" : "3.5"} width={landscape ? "17" : "10"} height={landscape ? "10" : "17"} rx="1.7" /></svg>;
  if (action === "preview") icon = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" /></svg>;
  if (action === "print") icon = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 8V3h10v5M7 17H5a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M7 14h10v7H7z" /></svg>;
  if (action === "export") icon = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12m0 0 5-5m-5 5-5-5M5 19h14" /></svg>;

  return <button type="button" className={buttonClass} onClick={onClick} aria-label={label} title={label} disabled={disabled}>{icon}</button>;
}

function AdvancePaymentDocumentEditor({ bookingId, reportBooking, payment, index, onPatch, onReplacePayments, onErrorChange, fiscalDocumentTypes = [], externalError = "", locked = false }) {
  const documentData = {
    ...EMPTY_BOOKING_PAYMENT.advanceDocument,
    ...(payment?.advanceDocument || {}),
    reversalDocument: {
      ...EMPTY_BOOKING_PAYMENT.advanceDocument.reversalDocument,
      ...(payment?.advanceDocument?.reversalDocument || {}),
    },
  };
  const reversalData = documentData.reversalDocument;
  const activeFiscalTypes = (fiscalDocumentTypes || []).filter((item) => item?.active !== false);
  const advanceDocumentTypes = activeFiscalTypes.filter((item) => (item.allowedPurposes || []).includes("ADVANCE") && ["INVOICE", "RECEIPT"].includes(String(item.code || "").toUpperCase()));
  const selectedAdvanceType = activeFiscalTypes.find((item) => String(item.code || "").toUpperCase() === String(documentData.documentType || "").toUpperCase());
  const configuredAdvanceReversalType = String(selectedAdvanceType?.reversalDocumentType || "").toUpperCase();
  const advanceReversalTypes = activeFiscalTypes.filter((item) => (item.allowedPurposes || []).includes("ADVANCE_REVERSAL") && (!configuredAdvanceReversalType || String(item.code || "").toUpperCase() === configuredAdvanceReversalType));
  const [numberError, setNumberError] = useState("");
  const [actionError, setActionError] = useState("");
  const [reversalNumberError, setReversalNumberError] = useState("");
  const [reversalActionError, setReversalActionError] = useState("");
  const [busy, setBusy] = useState(false);
  const [reversalBusy, setReversalBusy] = useState(false);
  const [previewKind, setPreviewKind] = useState("");
  const [printKind, setPrintKind] = useState("");
  const [exportKind, setExportKind] = useState("");
  const [exportError, setExportError] = useState("");
  const exportRootRef = useRef(null);
  const [numberingRefreshTick, setNumberingRefreshTick] = useState(0);
  const issued = documentData.issued === true && Boolean(String(documentData.documentId || "").trim());
  const reversalIssued = reversalData.issued === true && Boolean(String(reversalData.documentId || "").trim());
  const combinedError = numberError || actionError || reversalNumberError || reversalActionError || externalError;
  const numberFieldError = numberError || (/numero documento/i.test(actionError) ? actionError : "");
  const dateFieldError = /data documento/i.test(actionError) ? actionError : "";
  const generalActionError = ["condizione di pagamento", "importo acconto", "numero documento", "data documento"].some((token) => String(actionError || "").toLowerCase().includes(token))
    ? externalError
    : (actionError || externalError);
  const reversalNumberFieldError = reversalNumberError || (/numero documento/i.test(reversalActionError) ? reversalActionError : "");
  const reversalDateFieldError = /data documento/i.test(reversalActionError) ? reversalActionError : "";
  const reversalGeneralError = ["numero documento", "data documento"].some((token) => String(reversalActionError || "").toLowerCase().includes(token))
    ? ""
    : reversalActionError;

  useEffect(() => {
    onErrorChange?.(index, combinedError);
    return () => onErrorChange?.(index, "");
  }, [combinedError, index, onErrorChange]);

  useEffect(() => {
    const refresh = () => setNumberingRefreshTick((current) => current + 1);
    window.addEventListener(FISCAL_NUMBERING_REFRESH_EVENT, refresh);
    return () => window.removeEventListener(FISCAL_NUMBERING_REFRESH_EVENT, refresh);
  }, []);

  useEffect(() => {
    if (locked || issued || !documentData.emissionDate) return undefined;
    if (documentData.numberingMode === "MANUAL" && /^\d+$/.test(String(documentData.documentNumber || ""))) return undefined;
    let active = true;
    setNumberError("");
    getBookingDocumentNumberPreview({
      documentType: documentData.documentType,
      numberingMode: documentData.numberingMode,
      emissionDate: documentData.emissionDate,
      documentId: documentData.documentId || undefined,
    }).then((result) => {
      if (!active) return;
      onPatch({ documentNumber: String(result?.nextNumber || "") });
    }).catch((err) => {
      if (!active) return;
      setNumberError(err?.response?.data?.error || "Impossibile calcolare il Numero Documento");
      onPatch({ documentNumber: "" });
    });
    return () => { active = false; };
  }, [documentData.documentId, documentData.documentType, documentData.emissionDate, documentData.numberingMode, issued, locked, numberingRefreshTick]);

  useEffect(() => {
    if (locked || !issued || reversalIssued || !reversalData.emissionDate) return undefined;
    if (reversalData.numberingMode === "MANUAL" && /^\d+$/.test(String(reversalData.documentNumber || ""))) return undefined;
    let active = true;
    setReversalNumberError("");
    getBookingDocumentNumberPreview({
      documentType: reversalData.documentType,
      numberingMode: reversalData.numberingMode,
      emissionDate: reversalData.emissionDate,
      documentId: reversalData.documentId || undefined,
    }).then((result) => {
      if (!active) return;
      patchReversal({ documentNumber: String(result?.nextNumber || "") });
    }).catch((err) => {
      if (!active) return;
      setReversalNumberError(err?.response?.data?.error || "Impossibile calcolare il Numero Documento storno Acconto");
      patchReversal({ documentNumber: "" });
    });
    return () => { active = false; };
  }, [issued, locked, reversalData.documentId, reversalData.documentType, reversalData.emissionDate, reversalData.numberingMode, reversalIssued, numberingRefreshTick]);

  useEffect(() => {
    if (!locked && !documentData.emissionDate) onPatch({ emissionDate: currentHotelDateIso() });
  }, [locked]);

  useEffect(() => {
    if (locked || !issued || reversalData.emissionDate) return;
    patchReversal({ emissionDate: currentHotelDateIso() });
  }, [issued, locked, reversalData.emissionDate]);

  function patchReversal(patch) {
    onPatch({ reversalDocument: { ...reversalData, ...patch } });
  }

  function toggleOrientation() {
    onPatch({ orientation: documentData.orientation === "LANDSCAPE" ? "PORTRAIT" : "LANDSCAPE" });
  }

  function toggleReversalOrientation() {
    patchReversal({ orientation: reversalData.orientation === "LANDSCAPE" ? "PORTRAIT" : "LANDSCAPE" });
  }

  async function issueDocument() {
    if (locked) return;
    setActionError("");
    if (!bookingId) return setActionError("Salva prima la prenotazione per emettere il documento di acconto");
    if (!String(payment?.paymentMethod || "").trim()) return setActionError("Condizione di Pagamento obbligatoria");
    if (!(Number(payment?.amount || 0) > 0)) return setActionError("Importo Acconto obbligatorio");
    if (!documentData.emissionDate) return setActionError("Data Documento obbligatoria");
    if (!/^\d+$/.test(String(documentData.documentNumber || ""))) return setActionError("Numero Documento non valido");
    setBusy(true);
    try {
      const result = await issueBookingAdvanceDocument(bookingId, index, {
        payment: {
          paymentMethod: payment?.paymentMethod || "",
          paymentType: payment?.paymentType || "PAYMENT",
          notes: payment?.notes || "",
          amount: payment?.amount ?? 0,
          isDeposit: payment?.paymentType === "ADVANCE",
          advanceDocument: documentData,
        },
        documentType: documentData.documentType,
        numberingMode: documentData.numberingMode,
        documentNumber: documentData.documentNumber,
        emissionDate: documentData.emissionDate,
        note: documentData.note,
        orientation: documentData.orientation,
      });
      if (Array.isArray(result?.payments)) onReplacePayments(normalizeBookingPayments(result.payments));
      else onPatch({ documentId: result?.item?._id || "", documentNumber: String(result?.item?.documentNumber || documentData.documentNumber), issued: true });
      window.setTimeout(notifyFiscalNumberingRefresh, 0);
    } catch (err) {
      setActionError(err?.response?.data?.error || "Errore emissione documento acconto");
    } finally {
      setBusy(false);
    }
  }

  async function cancelDocument() {
    if (locked) return;
    setActionError("");
    if (!bookingId || reversalIssued) return;
    setBusy(true);
    try {
      const result = await cancelBookingAdvanceDocument(bookingId, index);
      if (Array.isArray(result?.payments)) onReplacePayments(normalizeBookingPayments(result.payments));
      window.setTimeout(notifyFiscalNumberingRefresh, 0);
    } catch (err) {
      setActionError(err?.response?.data?.error || "Errore annullamento documento acconto");
    } finally {
      setBusy(false);
    }
  }

  async function toggleReversalDocument() {
    if (locked) return;
    setReversalActionError("");
    if (!bookingId || !issued || reversalBusy) return;
    setReversalBusy(true);
    try {
      if (reversalIssued) {
        const result = await cancelBookingAdvanceReversalDocument(bookingId, index, reversalData.documentId);
        if (Array.isArray(result?.payments)) onReplacePayments(normalizeBookingPayments(result.payments));
        window.setTimeout(notifyFiscalNumberingRefresh, 0);
      } else {
        if (!reversalData.emissionDate) return setReversalActionError("Data Documento obbligatoria");
        if (!/^\d+$/.test(String(reversalData.documentNumber || ""))) return setReversalActionError("Numero Documento non valido");
        const result = await issueBookingAdvanceReversalDocument(bookingId, index, {
          documentType: reversalData.documentType,
          numberingMode: reversalData.numberingMode,
          documentNumber: reversalData.documentNumber,
          emissionDate: reversalData.emissionDate,
          note: reversalData.note,
          orientation: reversalData.orientation,
        });
        if (Array.isArray(result?.payments)) onReplacePayments(normalizeBookingPayments(result.payments));
        else if (result?.item) patchReversal({
          documentId: result.item._id || "",
          documentType: result.item.documentType || reversalData.documentType,
          documentNumber: String(result.item.documentSequence || result.item.documentNumber || reversalData.documentNumber || "").replace(/\D/g, ""),
          emissionDate: result.item.issuedAt ? String(result.item.issuedAt).slice(0, 10) : reversalData.emissionDate,
          orientation: result.item.orientation || reversalData.orientation,
          note: result.item.note ?? reversalData.note,
          issued: true,
        });
        window.setTimeout(notifyFiscalNumberingRefresh, 0);
      }
    } catch (err) {
      const item = err?.response?.data?.item;
      if (item) patchReversal({
        documentId: item._id || "",
        documentType: item.documentType || reversalData.documentType,
        documentNumber: String(item.documentSequence || item.documentNumber || "").replace(/\D/g, ""),
        emissionDate: item.issuedAt ? String(item.issuedAt).slice(0, 10) : reversalData.emissionDate,
        orientation: item.orientation || reversalData.orientation,
        note: item.note ?? reversalData.note,
        issued: true,
      });
      if (item) window.setTimeout(notifyFiscalNumberingRefresh, 0);
      setReversalActionError(err?.response?.data?.error || "Errore gestione storno Acconto");
    } finally {
      setReversalBusy(false);
    }
  }

  function printDocument(kind = "advance") {
    const target = kind === "reversal" ? reversalData : documentData;
    setPrintKind(kind);
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      try {
        printFiscalPreviewRoot({
          root: exportRootRef.current,
          orientation: target.orientation === "LANDSCAPE" ? "landscape" : "portrait",
          title: exportTitle(kind),
        });
      } finally {
        window.setTimeout(() => setPrintKind(""), 0);
      }
    }));
  }

  function exportDocument(kind = "advance") {
    setExportError("");
    setExportKind(kind);
  }

  function exportTitle(kind = exportKind) {
    const target = kind === "reversal" ? reversalData : documentData;
    if (kind === "reversal") return target.documentType === "RECEIPT_REVERSAL" ? "Storno Ricevuta Acconto" : "Nota di Credito Acconto";
    return target.documentType === "RECEIPT" ? "Ricevuta Acconto" : "Fattura Acconto";
  }

  async function handleDocumentExport(format) {
    const kind = exportKind || "advance";
    const target = kind === "reversal" ? reversalData : documentData;
    setExportError("");
    try {
      await new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
      const title = exportTitle(kind);
      const blob = await createFiscalVisualExportBlob({
        root: exportRootRef.current,
        format,
        orientation: target.orientation === "LANDSCAPE" ? "landscape" : "portrait",
      });
      const filename = `${slugify(title)}-${target.documentNumber || "documento"}.${format === "word" ? "docx" : "pdf"}`;
      downloadBlob(blob, filename);
      setExportKind("");
    } catch (_error) {
      setExportError("Impossibile esportare il documento");
    }
  }

  return (
    <div className="booking_advance_document_panel">
      <div className="booking_checkout_document_line">
        <label className="overlay_field booking_checkout_document_field">
          <OverlayFieldLabel required>Tipo Documento</OverlayFieldLabel>
          <VestaSelect disabled={locked || issued} value={documentData.documentType} onChange={(event) => {
            if (locked || issued) return;
            const documentType = event.target.value;
            onPatch({
              documentType,
              documentNumber: "",
              reversalDocument: {
                ...reversalData,
                documentType: activeFiscalTypes.find((item) => String(item.code || "").toUpperCase() === documentType)?.reversalDocumentType || (documentType === "RECEIPT" ? "RECEIPT_REVERSAL" : "CREDIT_NOTE"),
                documentNumber: "",
              },
            });
          }}>
            {(advanceDocumentTypes.length ? advanceDocumentTypes : [
              { code: "INVOICE", description: "Fattura" },
              { code: "RECEIPT", description: "Ricevuta" },
            ]).map((item) => <option key={item.code} value={item.code}>{item.description}</option>)}
          </VestaSelect>
        </label>
        <button
          type="button"
          tabIndex={-1}
          disabled={locked || issued}
          className={`booking_checkout_number_toggle${documentData.numberingMode === "MANUAL" ? " is_manual" : " is_auto"}`}
          onClick={() => !locked && !issued && onPatch({ numberingMode: documentData.numberingMode === "AUTO" ? "MANUAL" : "AUTO", documentNumber: "" })}
          aria-label={`Numerazione ${documentData.numberingMode === "AUTO" ? "automatica" : "manuale"}. Clicca per cambiare modalità`}
          aria-pressed={documentData.numberingMode === "MANUAL"}
        >
          <span className="booking_checkout_number_toggle_knob" aria-hidden="true" />
          <span className="booking_checkout_number_toggle_text">{documentData.numberingMode === "AUTO" ? "Automatico" : "Manuale"}</span>
        </button>
        <label className={overlayFieldClass(numberFieldError, "booking_checkout_number_field")}>
          <OverlayFieldLabel required>Numero Documento</OverlayFieldLabel>
          <input className="booking_checkout_number_input" value={documentData.documentNumber || ""} inputMode="numeric" pattern="[0-9]*" readOnly={locked || issued || documentData.numberingMode === "AUTO"} disabled={locked || issued} onChange={(event) => onPatch({ documentNumber: event.target.value.replace(/\D/g, "").slice(0, 9) })} />
          <OverlayFieldError message={numberFieldError} />
        </label>
        <label className={overlayFieldClass(dateFieldError, "booking_checkout_emission_date_field")}>
          <OverlayFieldLabel required>Data Documento</OverlayFieldLabel>
          <VestaDateInput value={documentData.emissionDate || ""} outputFormat="iso" disabled={locked || issued} onChange={(value) => !locked && !issued && onPatch({ emissionDate: value })} />
          <OverlayFieldError message={dateFieldError} />
        </label>
      </div>
      <div className="overlay_fields_grid booking_checkout_document_note_row">
        <label className="overlay_field overlay_field_full booking_checkout_note">
          <span>Note Documento</span>
          <DefaultNoteTextarea rows={3} value={documentData.note || ""} noteType="Documento" disabled={locked || issued} onChange={(value) => !locked && !issued && onPatch({ note: value })} />
        </label>
      </div>
      <div className="overlay_field booking_checkout_document_command_row">
        <div className="booking_checkout_icon_actions is_left" aria-label="Azioni preparazione documento acconto">
          <BookingFiscalActionIconButton action="orientation" orientation={documentData.orientation} onClick={toggleOrientation} />
          <BookingFiscalActionIconButton action="preview" onClick={() => setPreviewKind("advance")} disabled={issued || !documentData.documentNumber} />
        </div>
        <div className={`overlay_field booking_checkout_primary_action_field${generalActionError ? " is_invalid" : ""}`}>
          <OverlayFieldError message={generalActionError} />
          <button
            type="button"
            className={`overlay_save_button booking_checkout_primary_document_button${issued ? " is_cancel" : ""}`}
            onClick={issued ? cancelDocument : issueDocument}
            disabled={locked || busy || (issued && reversalIssued)}
            title={issued && reversalIssued ? "Annulla prima lo storno dell'Acconto" : undefined}
          >
            {busy ? "Operazione..." : issued ? "Annulla Documento" : "Emetti Documento"}
          </button>
        </div>
        <div className="booking_checkout_icon_actions is_right" aria-label="Azioni documento acconto emesso">
          <BookingFiscalActionIconButton action="print" onClick={() => printDocument("advance")} disabled={!issued} />
          <BookingFiscalActionIconButton action="export" onClick={() => exportDocument("advance")} disabled={!issued} />
        </div>
      </div>

      {issued && (
        <div className="booking_advance_reversal_block">
          <div className="booking_checkout_document_divider" />
          <div className="booking_checkout_document_line booking_checkout_reversal_document_line">
            <label className="overlay_field booking_checkout_document_field">
              <OverlayFieldLabel required>Tipo Documento Storno</OverlayFieldLabel>
              <VestaSelect disabled={locked || reversalIssued} value={reversalData.documentType} onChange={(event) => !locked && !reversalIssued && patchReversal({ documentType: event.target.value, documentNumber: "" })}>
                {(advanceReversalTypes.length ? advanceReversalTypes : [
                  { code: "CREDIT_NOTE", description: "Nota di Credito" },
                  { code: "RECEIPT_REVERSAL", description: "Storno Ricevuta" },
                ]).map((item) => <option key={item.code} value={item.code}>{item.description}</option>)}
              </VestaSelect>
            </label>
            <button
              type="button"
              tabIndex={-1}
              disabled={locked || reversalIssued}
              className={`booking_checkout_number_toggle${reversalData.numberingMode === "MANUAL" ? " is_manual" : " is_auto"}`}
              onClick={() => !locked && !reversalIssued && patchReversal({ numberingMode: reversalData.numberingMode === "AUTO" ? "MANUAL" : "AUTO", documentNumber: "" })}
              aria-label={`Numerazione storno Acconto ${reversalData.numberingMode === "AUTO" ? "automatica" : "manuale"}. Clicca per cambiare modalità`}
              aria-pressed={reversalData.numberingMode === "MANUAL"}
            >
              <span className="booking_checkout_number_toggle_knob" aria-hidden="true" />
              <span className="booking_checkout_number_toggle_text">{reversalData.numberingMode === "AUTO" ? "Automatico" : "Manuale"}</span>
            </button>
            <label className={overlayFieldClass(reversalNumberFieldError, "booking_checkout_number_field")}>
              <OverlayFieldLabel required>Numero Documento</OverlayFieldLabel>
              <input className="booking_checkout_number_input" value={reversalData.documentNumber || ""} inputMode="numeric" pattern="[0-9]*" readOnly={locked || reversalIssued || reversalData.numberingMode === "AUTO"} disabled={locked || reversalIssued} onChange={(event) => patchReversal({ documentNumber: event.target.value.replace(/\D/g, "").slice(0, 9) })} />
              <OverlayFieldError message={reversalNumberFieldError} />
            </label>
            <label className={overlayFieldClass(reversalDateFieldError, "booking_checkout_emission_date_field")}>
              <OverlayFieldLabel required>Data Documento</OverlayFieldLabel>
              <VestaDateInput value={reversalData.emissionDate || ""} outputFormat="iso" disabled={locked || reversalIssued} onChange={(value) => !locked && !reversalIssued && patchReversal({ emissionDate: value })} />
              <OverlayFieldError message={reversalDateFieldError} />
            </label>
          </div>
          <div className="overlay_fields_grid booking_checkout_document_note_row">
            <label className="overlay_field overlay_field_full booking_checkout_note">
              <span>Note Documento Storno</span>
              <DefaultNoteTextarea rows={3} value={reversalData.note || ""} noteType="Documento" disabled={locked || reversalIssued} onChange={(value) => !locked && !reversalIssued && patchReversal({ note: value })} />
            </label>
          </div>
          <div className="overlay_field booking_checkout_document_command_row is_reversal">
            <div className="booking_checkout_icon_actions is_left" aria-label="Azioni preparazione storno Acconto">
              <BookingFiscalActionIconButton action="orientation" orientation={reversalData.orientation} onClick={toggleReversalOrientation} />
              <BookingFiscalActionIconButton action="preview" onClick={() => setPreviewKind("reversal")} disabled={reversalIssued || !reversalData.documentNumber} title="Anteprima storno Acconto" />
            </div>
            <div className={`overlay_field booking_checkout_primary_action_field${reversalGeneralError ? " is_invalid" : ""}`}>
              <OverlayFieldError message={reversalGeneralError} />
              <button
                type="button"
                className={`overlay_save_button booking_checkout_primary_document_button${reversalIssued ? " is_cancel" : ""}`}
                onClick={toggleReversalDocument}
                disabled={locked || reversalBusy}
              >
                {reversalBusy ? "Operazione..." : reversalIssued ? "Riprendi Acconto - Annulla Documento" : "Storna Acconto - Emetti Documento"}
              </button>
            </div>
            <div className="booking_checkout_icon_actions is_right" aria-label="Azioni storno Acconto emesso">
              <BookingFiscalActionIconButton action="print" onClick={() => printDocument("reversal")} disabled={!reversalIssued} />
              <BookingFiscalActionIconButton action="export" onClick={() => exportDocument("reversal")} disabled={!reversalIssued} />
            </div>
          </div>
        </div>
      )}

      {previewKind && (
        <AdvancePaymentPreview
          booking={reportBooking || {}}
          payment={{ ...payment, advanceDocument: documentData }}
          documentKind={previewKind}
          onClose={() => setPreviewKind("")}
          onPrint={() => printDocument(previewKind)}
          onExport={() => exportDocument(previewKind)}
          onToggleOrientation={previewKind === "reversal" ? toggleReversalOrientation : toggleOrientation}
        />
      )}
      {(printKind || exportKind) && createPortal(
        <div className="booking_advance_print_portal" aria-hidden="true" ref={exportRootRef}>
          <AdvancePaymentFiscalReportPage
            booking={reportBooking || {}}
            payment={{ ...payment, advanceDocument: documentData }}
            documentKind={printKind || exportKind}
            printMode
          />
        </div>,
        window.document.body
      )}
      {createPortal(
        <UtilityExportOverlay
          open={Boolean(exportKind)}
          title={exportTitle()}
          documentMode
          formats={["pdf", "word"]}
          error={exportError}
          onClose={() => { setExportKind(""); setExportError(""); }}
          onExport={handleDocumentExport}
        />,
        window.document.body
      )}
    </div>
  );
}

function BookingPaymentsEditor({ payments, paymentMethodCatalog, fiscalDocumentTypes = [], onChange, bookingId = "", allowBalanceType = false, onErrorStateChange = null, externalError = "", locked = false }) {
  const rows = normalizeBookingPayments(payments, 0, "", true);
  const hasAdvancePayments = rows.some((payment) => String(payment?.paymentType || "").toUpperCase() === "ADVANCE");
  const [reportBooking, setReportBooking] = useState(null);
  const [loadedFiscalDocumentTypes, setLoadedFiscalDocumentTypes] = useState([]);
  const effectiveFiscalDocumentTypes = fiscalDocumentTypes.length ? fiscalDocumentTypes : loadedFiscalDocumentTypes;
  const [documentErrors, setDocumentErrors] = useState({});
  const reportDocumentError = useCallback((rowIndex, message) => {
    setDocumentErrors((current) => {
      const next = { ...current };
      if (message) next[rowIndex] = message;
      else delete next[rowIndex];
      return next;
    });
  }, []);

  useEffect(() => {
    onErrorStateChange?.(Object.values(documentErrors).some(Boolean));
  }, [documentErrors, onErrorStateChange]);

  useEffect(() => {
    if (fiscalDocumentTypes.length) return undefined;
    let active = true;
    getFiscalDocumentTypes().then((result) => {
      if (active) setLoadedFiscalDocumentTypes(Array.isArray(result?.items) ? result.items : []);
    }).catch(() => {
      if (active) setLoadedFiscalDocumentTypes([]);
    });
    return () => { active = false; };
  }, [fiscalDocumentTypes.length]);

  useEffect(() => {
    if (!bookingId || !hasAdvancePayments) {
      setReportBooking(null);
      return undefined;
    }
    let active = true;
    getBookingAccounts({ bookingId }).then((result) => {
      if (!active) return;
      const item = (result?.items || []).find((entry) => String(entry?._id || "") === String(bookingId));
      setReportBooking(item || null);
    }).catch(() => {
      if (active) setReportBooking(null);
    });
    return () => { active = false; };
  }, [bookingId, hasAdvancePayments]);

  function normalizedRows() {
    return rows.map((payment) => ({
      ...EMPTY_BOOKING_PAYMENT,
      ...payment,
      advanceDocument: {
        ...EMPTY_BOOKING_PAYMENT.advanceDocument,
        ...(payment.advanceDocument || {}),
        reversalDocument: {
          ...EMPTY_BOOKING_PAYMENT.advanceDocument.reversalDocument,
          ...(payment.advanceDocument?.reversalDocument || {}),
        },
      },
    }));
  }

  function updatePayment(index, field, value) {
    onChange(normalizedRows().map((payment, rowIndex) => {
      if (rowIndex !== index) return payment;
      if (field === "paymentType") {
        const paymentType = value;
        return {
          ...payment,
          paymentType,
          isDeposit: paymentType === "ADVANCE",
          advanceDocument: paymentType === "ADVANCE" ? {
            ...EMPTY_BOOKING_PAYMENT.advanceDocument,
            ...(payment.advanceDocument || {}),
            emissionDate: payment.advanceDocument?.emissionDate || currentHotelDateIso(),
            reversalDocument: {
              ...EMPTY_BOOKING_PAYMENT.advanceDocument.reversalDocument,
              ...(payment.advanceDocument?.reversalDocument || {}),
            },
          } : { ...EMPTY_BOOKING_PAYMENT.advanceDocument, reversalDocument: { ...EMPTY_BOOKING_PAYMENT.advanceDocument.reversalDocument } },
        };
      }
      return { ...payment, [field]: value };
    }));
  }

  function patchAdvanceDocument(index, patch) {
    onChange(normalizedRows().map((payment, rowIndex) => rowIndex === index ? {
      ...payment,
      advanceDocument: {
        ...EMPTY_BOOKING_PAYMENT.advanceDocument,
        ...(payment.advanceDocument || {}),
        ...patch,
        reversalDocument: patch.reversalDocument
          ? { ...EMPTY_BOOKING_PAYMENT.advanceDocument.reversalDocument, ...(payment.advanceDocument?.reversalDocument || {}), ...patch.reversalDocument }
          : { ...EMPTY_BOOKING_PAYMENT.advanceDocument.reversalDocument, ...(payment.advanceDocument?.reversalDocument || {}) },
      },
    } : payment));
  }

  function addPayment() {
    onChange([...normalizedRows(), {
      ...EMPTY_BOOKING_PAYMENT,
      advanceDocument: {
        ...EMPTY_BOOKING_PAYMENT.advanceDocument,
        reversalDocument: { ...EMPTY_BOOKING_PAYMENT.advanceDocument.reversalDocument },
      },
    }]);
  }

  function removePayment(index) {
    onChange(normalizedRows().filter((_, rowIndex) => rowIndex !== index));
  }

  const typeOptions = allowBalanceType ? BOOKING_PAYMENT_TYPE_OPTIONS : BOOKING_PAYMENT_TYPE_OPTIONS.filter((option) => option.value !== "BALANCE");

  return (
    <div className={`booking_related_guests_editor booking_payments_editor${locked ? " is_locked" : ""}`}>
      <div className="booking_related_guests_toolbar booking_payments_toolbar">
        <button type="button" className="booking_related_guests_add_button booking_payments_add_button overlay_list_add_button" onClick={addPayment} disabled={locked} aria-label="Aggiungi pagamento" title="Aggiungi pagamento" tabIndex={-1}>
          <svg viewBox="0 0 24 24" aria-hidden="true" className="overlay_action_icon overlay_add_icon"><path d="M12 5v14M5 12h14" /></svg>
        </button>
        <p>Registra Acconti, Caparre e Pagamenti. Il tipo A Saldo viene creato automaticamente solo dal Fast Check Out quando il relativo default sede è attivo.</p>
      </div>

      {rows.length === 0 ? (
        <div className="booking_related_guests_empty booking_payments_empty">Nessun pagamento inserito per questa prenotazione.</div>
      ) : (
        <div className="booking_related_guests_list booking_payments_list">
          {rows.map((payment, index) => {
            const advanceIssued = payment.paymentType === "ADVANCE" && payment?.advanceDocument?.issued === true;
            const isAutomaticBalance = payment.paymentType === "BALANCE";
            const documentError = String(documentErrors[index] || "");
            const paymentMethodError = /condizione di pagamento/i.test(documentError)
              ? documentError
              : (/completa i dati pagamenti/i.test(externalError) && !String(payment.paymentMethod || "").trim() ? "Condizione di Pagamento obbligatoria" : "");
            const amountError = /importo acconto/i.test(documentError)
              ? documentError
              : (/completa i dati pagamenti/i.test(externalError) && !Number.isFinite(Number(payment.amount)) ? "Importo non valido" : "");
            const advanceExternalError = payment.paymentType === "ADVANCE" && Number(payment.amount || 0) > 0 && payment?.advanceDocument?.issued !== true && /emetti.*accont/i.test(externalError)
              ? "Emetti il documento fiscale dell'Acconto"
              : (/pagamento selezionato non .+ acconto/i.test(externalError) && payment.paymentType === "ADVANCE" ? externalError : "");
            return (
              <div className={`booking_payment_group${advanceIssued ? " has_issued_document" : ""}`} key={`booking-payment-${index}`}>
                <div className="booking_related_guest_row booking_payment_row">
                  <OverlaySelect label="Condizione di Pagamento" value={payment.paymentMethod || ""} placeholder="Seleziona condizione" options={paymentMethodSelectOptions(paymentMethodCatalog, payment.paymentMethod)} disabled={locked} onChange={(value) => !locked && updatePayment(index, "paymentMethod", value)} error={paymentMethodError} />
                  <OverlaySelect label="Tipo" value={payment.paymentType || "PAYMENT"} placeholder="Seleziona tipo" options={isAutomaticBalance ? BOOKING_PAYMENT_TYPE_OPTIONS.filter((option) => option.value === "BALANCE") : typeOptions} disabled={locked || isAutomaticBalance} onChange={(value) => !locked && !isAutomaticBalance && updatePayment(index, "paymentType", value)} />
                  <label className="overlay_field booking_payment_notes"><span>Note</span><input value={payment.notes || ""} placeholder="Note relative al pagamento" disabled={locked} onChange={(event) => !locked && updatePayment(index, "notes", event.target.value)} onBlur={(event) => !locked && updatePayment(index, "notes", event.target.value.trim())} /></label>
                  <CurrencyField label="Importo" value={payment.amount} disabled={locked || isAutomaticBalance} preserveTyping onChange={(value) => !locked && !isAutomaticBalance && updatePayment(index, "amount", value)} error={amountError} />
                  <button type="button" className="overlay_contact_remove_button booking_related_guest_remove booking_payment_remove" onClick={() => removePayment(index)} aria-label="Elimina pagamento" title={isAutomaticBalance ? "Pagamento A Saldo generato dal Fast Check Out" : advanceIssued ? "Annulla prima il documento di acconto" : "Elimina pagamento"} tabIndex={-1} disabled={locked || advanceIssued || isAutomaticBalance}>×</button>
                </div>
                {payment.paymentType === "ADVANCE" && (
                  <AdvancePaymentDocumentEditor bookingId={bookingId} reportBooking={reportBooking ? { ...reportBooking, payments: rows } : null} payment={payment} index={index} onPatch={(patch) => patchAdvanceDocument(index, patch)} onReplacePayments={onChange} onErrorChange={reportDocumentError} fiscalDocumentTypes={effectiveFiscalDocumentTypes} externalError={advanceExternalError} locked={locked} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BookingOverlay({ mode, draft, error, onChange, onClose, onSave, onEditMaintenance, onDeleteMaintenance, onEditCleaning, onDeleteCleaning, onCustomerAction, onCustomerSelect, onRoomAction, roomCatalogs, rooms, sites, creditCardCircuits, paymentMethodCatalog, customers, customerTypeCatalog, bookingId = "", locked = false, activeStayTab = "booking", onStayTabChange, stayTabErrors = {}, onStayTabErrorChange = null }) {
  const [paymentEditorHasError, setPaymentEditorHasError] = useState(false);
  const isBookingForm = mode === "new" || mode === "edit";
  useEffect(() => {
    if (!isBookingForm || !onStayTabChange || !(error || paymentEditorHasError)) return;
    onStayTabErrorChange?.("booking", true);
  }, [error, isBookingForm, onStayTabChange, onStayTabErrorChange, paymentEditorHasError]);
  if (!mode) return null;
  const isCustomer = mode === "customer";
  const isCheckIn = mode === "checkin";
  const isAvailability = mode === "availability";
  const isRoomEdit = mode === "roomEdit";
  const isRoomMaintenance = mode === "roomMaintenance";
  const isRoomCleaning = mode === "roomCleaning";
  const isDirectMaintenanceEdit = isRoomMaintenance && draft.maintenance_context_edit === true;
  const isDirectCleaningEdit = isRoomCleaning && draft.cleaning_context_edit === true;
  const title = mode === "customer"
    ? "ANAGRAFICA CLIENTE"
    : mode === "edit"
      ? "MODIFICA PRENOTAZIONE"
      : mode === "checkin"
        ? "NUOVO CHECK-IN"
        : mode === "availability"
          ? "DISPONIBILITÀ CAMERA"
          : mode === "roomEdit"
            ? (draft._id ? "MODIFICA CAMERA" : "NUOVA CAMERA")
            : mode === "roomMaintenance"
              ? (isDirectMaintenanceEdit ? "MODIFICA MANUTENZIONE" : "GESTIONE MANUTENZIONE")
              : mode === "roomCleaning"
                ? (isDirectCleaningEdit ? "MODIFICA PULIZIA" : "GESTIONE PULIZIE")
                : "NUOVA PRENOTAZIONE";
  const isMaintenance = Boolean(draft.active_maintenance);
  const isBlockingCleaning = Boolean(draft.active_cleaning?.blocks_room !== false && !["DONE", "CANCELLED"].includes(String(draft.active_cleaning?.status || "").toUpperCase()));
  const roomReady = String(draft.cleaning_status || "") === "Pulizie ultimate" && !isMaintenance && !isBlockingCleaning;
  const availabilityCleaningLabel = isBlockingCleaning
    ? `Pulizia ${String(draft.active_cleaning?.status || "PLANNED").toUpperCase() === "IN_PROGRESS" ? "in corso" : "pianificata"}${draft.active_cleaning?.assigned_to ? ` · ${draft.active_cleaning.assigned_to}` : ""}`
    : (draft.cleaning_status || ROOM_STATUS_FALLBACK.cleaning);
  const privateCustomers = (customers || []).filter((customer) => getCustomerRegistryTypeFromCustomer(customer, customerTypeCatalog || [])?.code === "PRIVATO");
  const selectedCustomer = findCustomerById(customers || [], draft.customer_id);
  const customerPhone = selectedCustomer ? phoneMobileValue(selectedCustomer) : "";
  const customerEmail = selectedCustomer ? contactValue(selectedCustomer, ["Email"]) : "";
  const customerNotes = selectedCustomer ? customerRegistryNotes(selectedCustomer) : "";
  const customerFiscal = selectedCustomer ? customerFiscalReference(selectedCustomer) : "";
  const customerAddress = selectedCustomer ? customerAddressSummary(selectedCustomer) : "";
  const selectedInvoiceCustomer = findCustomerById(customers || [], draft.invoice_customer_id);
  const invoiceCustomerPhone = selectedInvoiceCustomer ? phoneMobileValue(selectedInvoiceCustomer) : "";
  const invoiceCustomerEmail = selectedInvoiceCustomer ? contactValue(selectedInvoiceCustomer, ["Email"]) : "";
  const invoiceCustomerNotes = selectedInvoiceCustomer ? customerRegistryNotes(selectedInvoiceCustomer) : "";
  const invoiceCustomerFiscal = selectedInvoiceCustomer ? customerFiscalReference(selectedInvoiceCustomer) : "";
  const invoiceCustomerAddress = selectedInvoiceCustomer ? customerAddressSummary(selectedInvoiceCustomer) : "";
  const selectedBookingRoom = (rooms || []).find((item) => String(item.room_number || "") === String(draft.room_number || ""));
  const selectedBookingRoomTypeCatalog = (roomCatalogs?.roomTypes || []).find((item) => String(item._id || "") === String(selectedBookingRoom?.room_type_id || ""));
  const selectedBookingRoomType = cleanGuestText(selectedBookingRoomTypeCatalog?.description || selectedBookingRoom?.room_type || draft.room_type) || "—";
  const selectedBookingRoomSite = (sites || []).find((item) => String(item._id || "") === String(draft.site_id || selectedBookingRoom?.site_id || ""));
  const selectedBookingRoomSiteLabel = cleanGuestText(selectedBookingRoomSite?.description || selectedBookingRoomSite?.name) || "—";
  const occupancyCounts = deriveBookingOccupancyCounts(draft, customers || []);
  const overlayTitle = isBookingForm && onStayTabChange ? "Soggiorno" : title;

  return (
    <div className="overlay_backdrop">
      <div className={`overlay_card booking_overlay_card${isBookingForm ? " booking_form_overlay_card" : ""}${mode === "edit" ? " booking_edit_overlay_card" : ""}${isBookingForm && onStayTabChange ? " booking_stay_overlay_card" : ""}`} role="dialog" aria-modal="true" aria-label={overlayTitle} onFocusCapture={selectOverlayFieldValue}>
        <div className="overlay_header">
          <h2>{overlayTitle}</h2>
          <button type="button" className="overlay_close_button" onClick={onClose} aria-label="Chiudi" tabIndex={-1}>×</button>
        </div>
        {isBookingForm && onStayTabChange && <BookingStayTabs activeTab={activeStayTab} onChange={onStayTabChange} checkoutDisabled={!(["CHECKED_IN", "CHECKED_OUT"].includes(normalizeStatus(draft.status)))} errorTabs={stayTabErrors} />}
        <div className="overlay_body booking_overlay_body">
          <fieldset className="booking_stay_locked_fieldset" disabled={locked && isBookingForm}>
          {isRoomEdit && (
            <div className="overlay_section">
              <h3>Dati camera</h3>
              <div className="overlay_fields_grid">
                <label className="overlay_field">
                  <OverlayFieldLabel required>Numero</OverlayFieldLabel>
                  <input value={draft.room_number || ""} onChange={(e) => onChange("room_number", e.target.value)} />
                </label>
                <OverlaySelect
                  label="Tipologia"
                  required
                  value={draft.room_type_id || ""}
                  placeholder="Seleziona tipologia camera"
                  options={(roomCatalogs?.roomTypes || []).map((item) => ({ value: item._id, label: item.description }))}
                  onChange={(value) => onChange("room_type_id", value)}
                />
                <label className="overlay_field">
                  <span>Prezzo</span>
                  <input type="number" min="0" step="0.01" value={draft.price ?? 0} onChange={(e) => onChange("price", e.target.value)} />
                </label>
                <OverlaySelect
                  label="Sede"
                  required
                  value={draft.site_id || ""}
                  placeholder="Seleziona sede"
                  options={(sites || []).filter((item) => item.active !== false).map((item) => ({ value: item._id, label: item.description }))}
                  onChange={(value) => onChange("site_id", value)}
                />
              </div>
            </div>
          )}

          {isRoomMaintenance && (
            <>
              <div className="overlay_section booking_maintenance_room_section">
                <h3>Dati camera</h3>
                <div className="overlay_fields_grid booking_maintenance_room_summary">
                  <label className="overlay_field">
                    <span>Camera</span>
                    <input value={draft.room_number || "—"} readOnly />
                  </label>
                  <label className="overlay_field">
                    <span>Tipologia</span>
                    <input value={selectedBookingRoomType} readOnly />
                  </label>
                  <label className="overlay_field">
                    <span>Sede</span>
                    <input value={selectedBookingRoomSiteLabel} readOnly />
                  </label>
                  <label className="overlay_field">
                    <span>Stato Pulizie</span>
                    <input value={draft.cleaning_status || selectedBookingRoom?.cleaning_status || ROOM_STATUS_FALLBACK.cleaning} readOnly />
                  </label>
                </div>

                {!isDirectMaintenanceEdit && (
                  <>
                    <h3>Storico manutenzioni camera {draft.room_number}</h3>
                    <div className="booking_maintenance_list">
                      {(draft.maintenances || []).length === 0 && (
                        <div className="booking_maintenance_empty">Nessuna manutenzione inserita</div>
                      )}
                      {(draft.maintenances || []).slice().sort((a, b) => new Date(b.date_start || 0) - new Date(a.date_start || 0)).map((item) => (
                        <div key={item._id} className="booking_maintenance_item">
                          <div>
                            <strong>{valueToDateTimeInput(item.date_start).replace("T", " ")} → {valueToDateTimeInput(item.date_end).replace("T", " ")}</strong>
                            <span>{item.description || "In Manutenzione"}{item.active === false ? " · Archiviata" : ""}</span>
                          </div>
                          {item.active !== false && (
                            <div className="booking_maintenance_actions">
                              <button type="button" onClick={() => onEditMaintenance(item)}>Modifica</button>
                              <button type="button" className="danger" onClick={() => onDeleteMaintenance(item._id)}>Elimina</button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="overlay_section booking_maintenance_data_section">
                <h3>Dati manutenzione</h3>
                <div className="overlay_fields_grid booking_maintenance_datetime_grid">
                  <label className="overlay_field">
                    <OverlayFieldLabel required>Data da</OverlayFieldLabel>
                    <VestaDateInput value={draft.maintenance_date_start || ""} outputFormat="iso" required onChange={(value) => onChange("maintenance_date_start", value)} />
                  </label>
                  <label className="overlay_field">
                    <OverlayFieldLabel required>Ora da</OverlayFieldLabel>
                    <VestaTimeInput value={draft.maintenance_time_start || ""} required ariaLabel="Ora da" onChange={(value) => onChange("maintenance_time_start", value)} />
                  </label>
                  <label className="overlay_field">
                    <OverlayFieldLabel required>Data a</OverlayFieldLabel>
                    <VestaDateInput value={draft.maintenance_date_end || ""} outputFormat="iso" required onChange={(value) => onChange("maintenance_date_end", value)} />
                  </label>
                  <label className="overlay_field">
                    <OverlayFieldLabel required>Ora a</OverlayFieldLabel>
                    <VestaTimeInput value={draft.maintenance_time_end || ""} required ariaLabel="Ora a" onChange={(value) => onChange("maintenance_time_end", value)} />
                  </label>
                  <label className="overlay_field overlay_field_full">
                    <span>Note Manutenzione</span>
                    <textarea value={draft.maintenance_description || ""} onChange={(e) => onChange("maintenance_description", e.target.value)} />
                  </label>
                </div>
              </div>
            </>
          )}

          {isRoomCleaning && (
            <>
              <div className="overlay_section booking_maintenance_room_section booking_cleaning_room_section">
                <h3>Dati camera</h3>
                <div className="overlay_fields_grid booking_maintenance_room_summary">
                  <label className="overlay_field"><span>Camera</span><input value={draft.room_number || "—"} readOnly /></label>
                  <label className="overlay_field"><span>Tipologia</span><input value={selectedBookingRoomType} readOnly /></label>
                  <label className="overlay_field"><span>Sede</span><input value={selectedBookingRoomSiteLabel} readOnly /></label>
                  <label className="overlay_field"><span>Stato Camera</span><input value={draft.cleaning_status || selectedBookingRoom?.cleaning_status || ROOM_STATUS_FALLBACK.cleaning} readOnly /></label>
                </div>

                {!isDirectCleaningEdit && (
                  <>
                    <h3>Storico pulizie camera {draft.room_number}</h3>
                    <div className="booking_maintenance_list booking_cleaning_list">
                      {(draft.cleanings || []).length === 0 && <div className="booking_maintenance_empty">Nessuna pulizia inserita</div>}
                      {(draft.cleanings || []).slice().sort((a, b) => new Date(b.date_start || 0) - new Date(a.date_start || 0)).map((item) => (
                        <div key={item._id} className="booking_maintenance_item booking_cleaning_item">
                          <div>
                            <strong>{valueToDateTimeInput(item.date_start).replace("T", " ")} → {valueToDateTimeInput(item.date_end).replace("T", " ")}</strong>
                            <span>{({ TURNOVER: "Cambio Ospite", STAYOVER: "Riassetto Soggiorno", DEEP: "Pulizia Profonda", INSPECTION: "Controllo" }[String(item.cleaning_type || "").toUpperCase()] || "Pulizia")} · {({ PLANNED: "Pianificata", IN_PROGRESS: "In corso", DONE: "Completata", CANCELLED: "Annullata" }[String(item.status || "").toUpperCase()] || item.status || "Pianificata")}{item.assigned_to ? ` · ${item.assigned_to}` : ""}{item.blocks_room === false ? " · Non bloccante" : ""}</span>
                          </div>
                          {item.active !== false && String(item.status || "").toUpperCase() !== "CANCELLED" && (
                            <div className="booking_maintenance_actions">
                              <button type="button" onClick={() => onEditCleaning?.(item)}>Modifica</button>
                              <button type="button" className="danger" onClick={() => onDeleteCleaning?.(item._id)}>Elimina</button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="overlay_section booking_maintenance_data_section booking_cleaning_data_section">
                <h3>Dati pulizia</h3>
                <div className="overlay_fields_grid booking_maintenance_datetime_grid">
                  <label className="overlay_field"><OverlayFieldLabel required>Data da</OverlayFieldLabel><VestaDateInput value={draft.cleaning_date_start || ""} outputFormat="iso" required onChange={(value) => onChange("cleaning_date_start", value)} /></label>
                  <label className="overlay_field"><OverlayFieldLabel required>Ora da</OverlayFieldLabel><VestaTimeInput value={draft.cleaning_time_start || ""} required ariaLabel="Ora pulizia da" onChange={(value) => onChange("cleaning_time_start", value)} /></label>
                  <label className="overlay_field"><OverlayFieldLabel required>Data a</OverlayFieldLabel><VestaDateInput value={draft.cleaning_date_end || ""} outputFormat="iso" required onChange={(value) => onChange("cleaning_date_end", value)} /></label>
                  <label className="overlay_field"><OverlayFieldLabel required>Ora a</OverlayFieldLabel><VestaTimeInput value={draft.cleaning_time_end || ""} required ariaLabel="Ora pulizia a" onChange={(value) => onChange("cleaning_time_end", value)} /></label>
                  <OverlaySelect label="Tipo Pulizia" required value={draft.cleaning_type || "TURNOVER"} options={[{ value: "TURNOVER", label: "Cambio Ospite" }, { value: "STAYOVER", label: "Riassetto Soggiorno" }, { value: "DEEP", label: "Pulizia Profonda" }, { value: "INSPECTION", label: "Controllo" }]} onChange={(value) => onChange("cleaning_type", value)} />
                  <OverlaySelect label="Stato" required value={draft.cleaning_task_status || "PLANNED"} options={[{ value: "PLANNED", label: "Pianificata" }, { value: "IN_PROGRESS", label: "In corso" }, { value: "DONE", label: "Completata" }, { value: "CANCELLED", label: "Annullata" }]} onChange={(value) => onChange("cleaning_task_status", value)} />
                  <OverlaySelect label="Priorità" required value={draft.cleaning_priority || "NORMAL"} options={[{ value: "LOW", label: "Bassa" }, { value: "NORMAL", label: "Normale" }, { value: "HIGH", label: "Alta" }, { value: "URGENT", label: "Urgente" }]} onChange={(value) => onChange("cleaning_priority", value)} />
                  <label className="overlay_field"><span>Assegnata a</span><input value={draft.cleaning_assigned_to || ""} placeholder="Addetto / Squadra" onChange={(event) => onChange("cleaning_assigned_to", event.target.value)} onBlur={(event) => onChange("cleaning_assigned_to", event.target.value.trim())} /></label>
                  <label className="overlay_field overlay_check_field"><span>Blocca disponibilità camera</span><input type="checkbox" checked={draft.cleaning_blocks_room !== false} onChange={(event) => onChange("cleaning_blocks_room", event.target.checked)} /></label>
                  <label className="overlay_field overlay_field_full"><span>Note Pulizia</span><textarea value={draft.cleaning_note || ""} onChange={(event) => onChange("cleaning_note", event.target.value)} onBlur={(event) => onChange("cleaning_note", event.target.value.trim())} /></label>
                </div>
              </div>
            </>
          )}

          {isAvailability && (
            <>
              <div className="overlay_section booking_availability_reference_section">
                <h3>Riferimento</h3>
                <div className="overlay_fields_grid">
                  <label className="overlay_field">
                    <span>Camera</span>
                    <input value={draft.room_number} readOnly />
                  </label>
                  <label className="overlay_field">
                    <span>Data riferimento</span>
                    <input value={draft.check_in} readOnly />
                  </label>
                  <label className="overlay_field">
                    <span>Ora riferimento</span>
                    <input value={draft.check_in_time || "12:00"} readOnly />
                  </label>
                </div>
              </div>
              <div className="overlay_section booking_availability_status_section">
                <h3>Stato disponibilità</h3>
                <div className={`booking_availability_banner ${roomReady ? "is_available" : "is_unavailable"}`}>
                  {roomReady ? "Disponibile" : "Non disponibile"}
                </div>
                <div className="booking_availability_status_grid">
                  <article>
                    <span>Pulizie</span>
                    <strong>{availabilityCleaningLabel}</strong>
                  </article>
                  <article>
                    <span>Manutenzione</span>
                    <strong>{isMaintenance ? `In manutenzione${draft.active_maintenance?.description ? ` - ${draft.active_maintenance.description}` : ""}` : ROOM_STATUS_FALLBACK.maintenance}</strong>
                  </article>
                </div>
              </div>
            </>
          )}

          {!isAvailability && !isRoomEdit && !isRoomMaintenance && !isRoomCleaning && (
            <div className="overlay_section">
            <h3>Cliente Ospite</h3>
            <div className="overlay_fields_grid">
              <CustomerAutocompleteField
                label="Ragione sociale"
                value={draft.guest_name}
                customerId={draft.customer_id}
                customers={privateCustomers}
                onChange={(value) => onChange("guest_name", value)}
                onSelect={(customer) => onCustomerSelect?.("customer", customer)}
                onAction={() => onCustomerAction("customer")}
              />
              <label className="overlay_field overlay_check_field booking_invoice_same_field">
                <span>Cliente e Intestatario Fattura sono Uguali</span>
                <input type="checkbox" checked={draft.invoice_same_as_customer !== false} onChange={(e) => onChange("invoice_same_as_customer", e.target.checked)} />
              </label>
              <div className="booking_customer_quick_info overlay_field_full">
                <div className="booking_customer_info_item">
                  <span>Codice Fiscale / Partita IVA</span>
                  <strong>{customerFiscal || "—"}</strong>
                </div>
                <div className="booking_customer_info_item booking_customer_info_address">
                  <span>Indirizzo</span>
                  <strong>{customerAddress || "—"}</strong>
                </div>
                <div className="booking_customer_info_item">
                  <span>Telefono / cellulare</span>
                  <strong>{customerPhone || "—"}</strong>
                </div>
                <div className="booking_customer_info_item">
                  <span>Mail</span>
                  <strong>{customerEmail || "—"}</strong>
                </div>
                <div className="booking_customer_info_item booking_customer_info_notes">
                  <span>Note</span>
                  <div className="booking_customer_info_notes_value">{customerNotes || "—"}</div>
                </div>
              </div>
            </div>
            </div>
          )}

          {!isAvailability && !isRoomEdit && !isRoomMaintenance && !isRoomCleaning && draft.invoice_same_as_customer === false && (
            <div className="overlay_section">
              <h3>Intestatario Fattura</h3>
              <div className="overlay_fields_grid">
                <CustomerAutocompleteField
                  label="Ragione sociale"
                  value={draft.invoice_name}
                  customerId={draft.invoice_customer_id}
                  customers={customers || []}
                  onChange={(value) => onChange("invoice_name", value)}
                  onSelect={(customer) => onCustomerSelect?.("invoice", customer)}
                  onAction={() => onCustomerAction("invoice")}
                />
                <div className="booking_customer_quick_info overlay_field_full">
                  <div className="booking_customer_info_item">
                    <span>Codice Fiscale / Partita IVA</span>
                    <strong>{invoiceCustomerFiscal || "—"}</strong>
                  </div>
                  <div className="booking_customer_info_item booking_customer_info_address">
                    <span>Indirizzo</span>
                    <strong>{invoiceCustomerAddress || "—"}</strong>
                  </div>
                  <div className="booking_customer_info_item">
                    <span>Telefono / cellulare</span>
                    <strong>{invoiceCustomerPhone || "—"}</strong>
                  </div>
                  <div className="booking_customer_info_item">
                    <span>Mail</span>
                    <strong>{invoiceCustomerEmail || "—"}</strong>
                  </div>
                  <div className="booking_customer_info_item booking_customer_info_notes">
                    <span>Note</span>
                    <div className="booking_customer_info_notes_value">{invoiceCustomerNotes || "—"}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isCustomer && !isAvailability && !isRoomEdit && !isRoomMaintenance && !isRoomCleaning && (
            <div className="overlay_section">
              <h3>Ospiti Correlati</h3>
              <AdditionalGuestsEditor
                guests={draft.relatedGuests || []}
                customers={privateCustomers}
                primaryCustomerId={draft.customer_id}
                referenceDate={draft.check_in}
                onCustomerAction={onCustomerAction}
                onChange={(value) => onChange("relatedGuests", value)}
              />
            </div>
          )}

          {!isCustomer && !isAvailability && !isRoomEdit && !isRoomMaintenance && !isRoomCleaning && (
            <div className="overlay_section">
              <h3>Prenotazione</h3>
              <div className="overlay_fields_grid">
                <FieldWithAction
                  label="Camera"
                  required
                  value={draft.room_number}
                  onChange={(value) => onChange("room_number", value)}
                  onAction={onRoomAction}
                  actionIcon={(rooms || []).some((item) => String(item.room_number || "") === String(draft.room_number || "")) ? "✎" : "+"}
                />
                <OverlaySelect
                  label="Stato"
                  value={draft.status}
                  placeholder="Seleziona stato prenotazione"
                  options={[
                    { value: "PENDING", label: "In attesa" },
                    { value: "CONFIRMED", label: "Confermata" },
                    { value: "CHECKED_IN", label: "In house" },
                    { value: "CHECKED_OUT", label: "Conclusa" },
                    { value: "CANCELLED", label: "Cancellata" },
                  ]}
                  onChange={(value) => onChange("status", value)}
                  disabled={normalizeStatus(draft._originalStatus || draft.status) === "CHECKED_OUT"}
                />
                <DateField label="Check In" required value={draft.check_in} onChange={(value) => onChange("check_in", value)} />
                <label className="overlay_field"><OverlayFieldLabel required>Ora Check In</OverlayFieldLabel><VestaTimeInput value={draft.check_in_time || "12:00"} required ariaLabel="Ora Check In" onChange={(value) => onChange("check_in_time", value)} /></label>
                <DateField label="Check Out" required value={draft.check_out} onChange={(value) => onChange("check_out", value)} />
                <label className="overlay_field"><OverlayFieldLabel required>Ora Check Out</OverlayFieldLabel><VestaTimeInput value={draft.check_out_time || "11:59"} required ariaLabel="Ora Check Out" onChange={(value) => onChange("check_out_time", value)} /></label>

                <div className="booking_reservation_quick_info overlay_field_full">
                  <div className="booking_reservation_info_item">
                    <span>Tipologia</span>
                    <strong>{selectedBookingRoomType}</strong>
                  </div>
                  <div className="booking_reservation_info_item">
                    <span>Adulti</span>
                    <strong>{occupancyCounts.adults}</strong>
                  </div>
                  <div className="booking_reservation_info_item">
                    <span>Bambini</span>
                    <strong>{occupancyCounts.children}</strong>
                  </div>
                  <div className="booking_reservation_info_item">
                    <span>Animali</span>
                    <strong>{occupancyCounts.animals}</strong>
                  </div>
                </div>

              </div>
            </div>
          )}

          {isBookingForm && (
            <div className="overlay_section">
              <h3>Pagamenti</h3>
              <BookingPaymentsEditor
                payments={draft.payments || []}
                paymentMethodCatalog={paymentMethodCatalog || []}
                bookingId={bookingId || draft._id || ""}
                onChange={(value) => onChange("payments", value)}
                onErrorStateChange={setPaymentEditorHasError}
                externalError={/pagament|accont/i.test(String(error || "")) ? error : ""}
                locked={locked}
              />
            </div>
          )}

          {isBookingForm && (
            <div className="overlay_section">
              <h3>Dati Aggiuntivi</h3>
              <div className="overlay_fields_grid">
                <label className="overlay_field overlay_field_full booking_reservation_notes_field">
                  <span>Note Prenotazione</span>
                  <DefaultNoteTextarea value={draft.notes} noteType="Prenotazione" onChange={(value) => onChange("notes", value)} />
                </label>
              </div>
            </div>
          )}

          {isCheckIn && !isRoomEdit && !isRoomMaintenance && !isRoomCleaning && (
            <div className="overlay_section">
              <h3>Check In</h3>
              <div className="overlay_fields_grid">
                <OverlaySelect
                  label="Documento"
                  value={draft.document_type || CHECKIN_DEFAULTS.document_type}
                  placeholder="Seleziona documento"
                  options={descriptionCatalogOptions(roomCatalogs?.documentTypes || [], draft.document_type || CHECKIN_DEFAULTS.document_type)}
                  onChange={(value) => onChange("document_type", value)}
                />
                <label className="overlay_field">
                  <span>Numero Documento</span>
                  <input value={draft.document_number || ""} onChange={(e) => onChange("document_number", e.target.value)} />
                </label>
                <OverlaySelect
                  label="Metodo di Pagamento"
                  value={draft.payment_method || ""}
                  placeholder="Seleziona metodo di pagamento"
                  options={catalogSelectOptions(paymentMethodCatalog, draft.payment_method)}
                  onChange={(value) => onChange("payment_method", value)}
                />
                <CurrencyField label="Totale" value={draft.total_amount} onChange={(value) => onChange("total_amount", value)} />
              </div>
            </div>
          )}

          {isCheckIn && !isRoomEdit && !isRoomMaintenance && !isRoomCleaning && (
            <div className="overlay_section">
              <h3>Carta di Credito</h3>
              <div className="overlay_fields_grid">
                <label className="overlay_field">
                  <span>Numero</span>
                  <input inputMode="numeric" value={draft.credit_card_number || ""} onChange={(e) => onChange("credit_card_number", formatCreditCardNumber(e.target.value))} />
                </label>
                <OverlaySelect
                  label="Circuito"
                  value={draft.credit_card_circuit_id || draft.credit_card || ""}
                  placeholder="Seleziona circuito"
                  options={(creditCardCircuits || []).map((item) => ({ value: item._id, label: item.description }))}
                  onChange={(value) => {
                    const selectedCircuit = (creditCardCircuits || []).find((item) => String(item._id) === String(value));
                    onChange("credit_card_circuit_id", value);
                    onChange("credit_card", selectedCircuit?.description || "");
                  }}
                />
                <MonthField label="Scadenza" value={draft.credit_card_expiry || ""} onChange={(value) => onChange("credit_card_expiry", value)} />
              </div>
            </div>
          )}
          </fieldset>
        </div>
        <div className="overlay_footer">
          <div className="overlay_error_message">{isBookingForm && onStayTabChange ? ((error || paymentEditorHasError) ? "Impossibile Completare l’Azione Richiesta" : "") : error}</div>
          {isAvailability ? (
            <button type="button" className="overlay_save_button" onClick={onClose}>Chiudi</button>
          ) : mode === "edit" ? (
            <button type="button" className="overlay_save_button overlay_save_close_button" disabled={locked && isBookingForm} onClick={onSave}>Salva e Chiudi</button>
          ) : (
            <button type="button" className="overlay_save_button overlay_save_close_button" disabled={locked && isBookingForm} onClick={onSave}>Salva e Chiudi</button>
          )}
        </div>
      </div>
    </div>
  );
}


export {
  ROOM_GROUPS,
  EMPTY_ROOM_DRAFT,
  MONTHS,
  WEEKDAYS,
  STATUS_LABELS,
  STATUS_FILTER_OPTIONS,
  STATUS_ALL_DOT_ORDER,
  EMPTY_BOOKING,
  CHECKIN_DEFAULTS,
  ROOM_STATUS_FALLBACK,
  EMPTY_CUSTOMER,
  startOfDay,
  addDays,
  addMonths,
  toIsoDate,
  toIsoDateTime,
  toCheckInDateTime,
  toCheckOutDateTime,
  valueToDateTimeInput,
  valueToDateInput,
  formatDateForInput,
  parseFlexibleDate,
  normalizeDateFieldValue,
  toIsoDateFromInput,
  toCheckInDateTimeFromInput,
  toCheckOutDateTimeFromInput,
  formatRangeMonth,
  formatMoneyFieldValue,
  normalizeMoneyToNumber,
  formatCreditCardNumber,
  normalizeMonthFieldValue,
  toMonthInputValue,
  normalizeStatus,
  isCancelledStatus,
  getBookingName,
  money,
  getBookingId,
  bookingToDraft,
  normalizeBookingPayments,
  notifyFiscalNumberingRefresh,
  BookingPaymentsEditor,
  deriveBookingOccupancyCounts,
  getRoomGroups,
  BookingOverlay,
};
